import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faSearch,
  faList,
  faFileImport,
  faProcedures,
  faStethoscope,
  faHeartbeat,
  faFileAlt,
  faClock,
  faHeart,
  faChevronUp,
  faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import { fetchPracticePatientsFilteredByProgramTab, fetchPracticeSummary } from '../../../store/slices/practiceSlice';
import { deletePatient } from '../../../store/slices/patientSlice';
import patientService from '../../../services/patientService';
import practiceService from '../../../services/practiceService';
import socketService from '../../../services/socketService';
import { toast } from 'react-toastify';
import { ADD_PATIENT_QUERY_FLAG, appendFromDashboardQuery, isFromPracticeDashboard } from '../../../utils/practiceNavigation';
import { canDeletePatient } from '../../../utils/roleUtils';
import { normalizePatientStatusId, patientStatusLabel, getPatientStatusSortOrder, resolvePatientStatusRaw } from '../../../utils/patientStatusUtils';
import { formatChronicDiseaseNames } from '../../../utils/patientBillingUtils';
import {
  resolveProgramCptDefinitions,
  buildCptTilesWithCounts,
  buildProgramTabsWithCpt
} from './programAnalyticsConfig';
import './PatientProgramAnalytics.css';
import { computeTotalPages } from '../../../utils/paginationUtils';
import { formatPhoneForDisplay } from '../../../utils/phoneUtils';
import Table from '../../../components/Common/Table/Table';
import StatusBadge from '../../../components/Common/StatusBadge/StatusBadge';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import SearchBar from '../../../components/Common/SearchBar/SearchBar';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import { formatDisplayError } from '../../../utils/passwordValidation';
import './PatientManagement.css';
import './ProviderPatientDesignC.css';
import Trend7dCell from '../../../components/Trend7dCell/Trend7dCell';
import PatientReviewCell from '../../../components/PatientReviewCell/PatientReviewCell';
import { formatDateOnlyUs } from '../../../utils/dateOnlyUtils';
import { AddPatientModal } from './AddPatient/AddPatientModal';
import {
  checkBPValues,
  checkBGValue,
  checkWeightValue,
  getMeasurementAlertStatus,
  kgToLb
} from '../../../utils/measurementUtils';
import pulseIcon from '../../../assets/pulse.png';
import PulseIcon from '../../../components/Common/PulseIcon/PulseIcon';

function resolvePatientListLimit(entriesPerPage) {
  if (entriesPerPage === 'all' || entriesPerPage === 'All') return 'all';
  const parsed = parseInt(entriesPerPage, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 10;
}

const patientListPageSizeByPractice = new Map();

function rememberPatientListPageSize(practiceId, pageSize) {
  if (!practiceId) return;
  patientListPageSizeByPractice.set(String(practiceId), pageSize);
}

function readRememberedPatientListPageSize(practiceId) {
  if (!practiceId) return 10;
  return patientListPageSizeByPractice.get(String(practiceId)) ?? 10;
}

function programTabIdsKey(tabs) {
  if (!Array.isArray(tabs) || tabs.length === 0) return '';
  return tabs.map((tab) => tab.id).join('|');
}

const LIST_VITAL_TARGETS = {
  bloodPressure: {
    systolicMin: 100,
    systolicMax: 140,
    diastolicMin: 60,
    diastolicMax: 90
  },
  bloodGlucose: {
    minimum: 60,
    maximum: 110
  },
  weight: {
    weightMin: 66,
    weightMax: 220
  },
  pulse: {
    min: 60,
    max: 100
  }
};

function vitalAlertClass(status) {
  if (status === 'high') return 'alert-high';
  if (status === 'low') return 'alert-low';
  if (status === 'normal') return 'alert-normal';
  return '';
}

function renderVitalValue(value, status) {
  if (value == null || value === '' || value === 'N/A' || value === 0 || value === '0' || Number(value) <= 0 || Number.isNaN(Number(value))) return 'N/A';
  const cls = vitalAlertClass(status);
  if (!cls) return value;
  return <span className={`patient-management-vital-value ${cls}`}>{value}</span>;
}

function renderPulseValue(value, status) {
  if (value == null || value === '' || value === 'N/A' || value === 0 || value === '0' || Number(value) <= 0 || Number.isNaN(Number(value))) return 'N/A';
  const cls = vitalAlertClass(status);
  const isAbnormal = status === 'high' || status === 'low';
  return (
    <span className={`patient-management-pulse-with-icon patient-management-vital-value ${cls}`}>
      {value}
      <PulseIcon isAbnormal={isAbnormal} className="patient-management-pulse-icon" />
    </span>
  );
}

function getPatientListVitalStatuses(patient) {
  const sys = patient?.last_systolic ?? patient?.systolic;
  const dia = patient?.last_diastolic ?? patient?.diastolic;
  const pulse = patient?.last_pulse ?? patient?.pulse;
  const glucose = patient?.last_glucose ?? patient?.glucose;
  const weightKg = patient?.last_weight ?? patient?.weight;

  const sysNum = sys != null && sys !== 'N/A' ? Number(sys) : null;
  const diaNum = dia != null && dia !== 'N/A' ? Number(dia) : null;

  const bpTarget = {
    systolicMin: patient?.target_sys_min ?? LIST_VITAL_TARGETS.bloodPressure.systolicMin,
    systolicMax: patient?.target_sys_max ?? LIST_VITAL_TARGETS.bloodPressure.systolicMax,
    diastolicMin: patient?.target_dia_min ?? LIST_VITAL_TARGETS.bloodPressure.diastolicMin,
    diastolicMax: patient?.target_dia_max ?? LIST_VITAL_TARGETS.bloodPressure.diastolicMax,
  };

  const bpStatus = checkBPValues(
    {
      systolic_pressure: sysNum != null && !Number.isNaN(sysNum) && sysNum > 0 ? sysNum : null,
      diastolic_pressure: diaNum != null && !Number.isNaN(diaNum) && diaNum > 0 ? diaNum : null
    },
    bpTarget
  );

  const bgTarget = {
    minimum: patient?.target_bg_min ?? LIST_VITAL_TARGETS.bloodGlucose.minimum,
    maximum: patient?.target_bg_max ?? LIST_VITAL_TARGETS.bloodGlucose.maximum,
  };

  const weightTarget = {
    weightMin: patient?.target_weight_min ?? LIST_VITAL_TARGETS.weight.weightMin,
    weightMax: patient?.target_weight_max ?? LIST_VITAL_TARGETS.weight.weightMax,
  };

  const pulseNum = pulse != null && pulse !== 'N/A' ? Number(pulse) : null;
  const pulseMin = patient?.target_pulse_min ?? patient?.pulse_min ?? LIST_VITAL_TARGETS.pulse.min;
  const pulseMax = patient?.target_pulse_max ?? patient?.pulse_max ?? LIST_VITAL_TARGETS.pulse.max;
  const pulseStatus =
    pulseNum == null || Number.isNaN(pulseNum) || pulseNum <= 0
      ? 'normal'
      : getMeasurementAlertStatus(pulseNum, pulseMin, pulseMax);

  const glucoseNum = glucose != null && glucose !== 'N/A' ? Number(glucose) : null;
  const glucoseStatus =
    glucoseNum == null || Number.isNaN(glucoseNum) || glucoseNum <= 0
      ? 'normal'
      : checkBGValue(glucoseNum, bgTarget);

  const weightNum = weightKg != null && weightKg !== 'N/A' ? Number(weightKg) : null;
  const weightStatus =
    weightNum == null || Number.isNaN(weightNum) || weightNum <= 0
      ? 'normal'
      : checkWeightValue(weightNum, weightTarget);

  return {
    sysStatus: bpStatus.sysStatus,
    diaStatus: bpStatus.diaStatus,
    pulseStatus,
    glucoseStatus,
    weightStatus
  };
}

function getProgramTabIcon(tab) {
  const l = String(tab?.label ?? '').toUpperCase();
  if (l.includes('RPM')) return faHeartbeat;
  if (l.includes('CCM')) return faFileAlt;
  if (l.includes('PCM')) return faStethoscope;
  if (l.includes('RTM')) return faClock;
  if (l.includes('CARDIAC')) return faHeart;
  return faProcedures;
}

/** KPI cards that are active-patient-only — hide status dropdown on these views. */
const RPM_UPLOAD_KPI_FILTERS = new Set(['recentUploads', 'missedUploads', 'abnormalMeasurements']);

/** Unwrap { success, data } from axios practice API responses. */
function extractPracticeFromApiResponse(response) {
  const body = response?.data;
  if (body?.success && body?.data) return body.data;
  return null;
}

function getCachedProgramTabsForPractice(currentPractice, practiceId) {
  if (!practiceId || !currentPractice) return [];
  if (String(currentPractice.id) !== String(practiceId)) return [];
  const enrolled = currentPractice.enrolled_programs;
  if (!Array.isArray(enrolled) || enrolled.length === 0) return [];
  return buildProgramTabsWithCpt(enrolled);
}

function resolveDefaultProgramTabId(programTabs) {
  if (!programTabs.length) return '';
  const rpmTab = programTabs.find(
    (tab) => tab.id === 'rpm' || String(tab.label || '').trim().toLowerCase() === 'rpm'
  );
  return rpmTab ? rpmTab.id : programTabs[0].id;
}

function resolveInitialProgramTab(programTabs, searchString) {
  if (!programTabs.length) return '';
  const params = new URLSearchParams(searchString || '');
  const urlProgram = String(params.get('program') || '').trim().toLowerCase();
  const matched = programTabs.find((tab) => tab.id === urlProgram);
  return matched ? matched.id : resolveDefaultProgramTabId(programTabs);
}

/** `patients.id` when present — aligns list actions with PatientEditView and PUT /patients/:id. */
function resolvePatientRecordId(patient) {
  if (!patient) return null;
  const pid = patient.patient_table_id ?? patient.id;
  console.debug('[PatientManagement] Resolved Patient ID:', pid, 'from object:', patient);
  return pid;
}

/** Fixed column widths for embedded program lists — keeps header height/width stable across RPM/CCM/PCM */
const EMBEDDED_RPM_COLUMN_WIDTHS = {
  idx: '4%',
  status: '6%',
  vitals: '9%',
  name: '13%',
  systolic: '7%',
  diastolic: '7%',
  pulse: '6%',
  glucose: '7%',
  weight: '7%',
  last_upload: '11%',
  readings: '6%',
  service_time: '9%',
  act: '8%'
};

const EMBEDDED_CCM_COLUMN_WIDTHS = {
  idx: '5%',
  status: '8%',
  name: '16%',
  dob: '10%',
  chronic_conditions: '18%',
  last_activity: '12%',
  service_time: '12%',
  billing: '10%',
  act: '9%'
};

const EMBEDDED_PCM_COLUMN_WIDTHS = {
  idx: '5%',
  status: '8%',
  name: '18%',
  primary_diagnosis: '18%',
  specialist: '14%',
  monthly_time: '13%',
  last_review: '13%',
  act: '11%'
};

const EMBEDDED_PROGRAM_COLUMN_WIDTHS = {
  rpm: EMBEDDED_RPM_COLUMN_WIDTHS,
  ccm: EMBEDDED_CCM_COLUMN_WIDTHS,
  pcm: EMBEDDED_PCM_COLUMN_WIDTHS
};

function applyEmbeddedProgramColumnWidths(columns, programKey, enabled) {
  if (!enabled) return columns;
  const map = EMBEDDED_PROGRAM_COLUMN_WIDTHS[programKey] || EMBEDDED_RPM_COLUMN_WIDTHS;
  return columns.map((col) => ({
    ...col,
    width: map[col.key] || col.width || '8%'
  }));
}

const PatientManagement = ({
  practiceId,
  isReadOnly = false,
  isDashboardView = false,
  embeddedInPracticeDashboard = false,
  /** Provider / caregiver dashboard — ERMP Design C patient list layout */
  embeddedInProviderDashboard = false,
  embeddedInCaregiverDashboard = false,
  /** Parent dashboard tab to keep in URL (e.g. "patient") so ?program= does not reset navigation */
  dashboardTabKey = null,
  /** When embedded in a multi-tab dashboard, false while this pane is hidden (keep-alive). */
  isDashboardTabActive = true,
  onOpenEventLogs = null,
  hideEventLogsButton = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useSelector((state) => state.auth || {});
  const { currentPractice } = useSelector((state) => state.practice ?? {});
  const {
    practicePatients = [],
    practicePatientsProgram = null,
    practicePatientsPracticeId = null,
    listLoading = {},
    error,
    pagination
  } = useSelector((state) => state.practice ?? {});
  const patientsLoading = listLoading.patientsByProgram;
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(() => readRememberedPatientListPageSize(practiceId));
  const [localStatusFilter, setLocalStatusFilter] = useState(null);
  const [initialTableLoading, setInitialTableLoading] = useState(
    () => getCachedProgramTabsForPractice(currentPractice, practiceId).length === 0
  );
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [dashboardPatients, setDashboardPatients] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [bucketLoading, setBucketLoading] = useState(false);
  const [activeDashboardTable, setActiveDashboardTable] = useState(null);
  const [dashboardCurrentPage, setDashboardCurrentPage] = useState(1);
  const [dashboardEntriesPerPage, setDashboardEntriesPerPage] = useState('10');
  const [dashboardBucketTotal, setDashboardBucketTotal] = useState(0);
  const [dashboardServerPaged, setDashboardServerPaged] = useState(false);
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [patientToEdit, setPatientToEdit] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [analyticsExpanded, setAnalyticsExpanded] = useState(true);
  const [showBillingModal, setShowBillingModal] = useState(false);

  useEffect(() => {
    if (!showBillingModal) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showBillingModal]);

  const [programTabs, setProgramTabs] = useState(() =>
    getCachedProgramTabsForPractice(currentPractice, practiceId)
  );
  const [programsReady, setProgramsReady] = useState(
    () => getCachedProgramTabsForPractice(currentPractice, practiceId).length > 0
  );
  const [activeProgramTab, setActiveProgramTab] = useState(() => {
    const tabs = getCachedProgramTabsForPractice(currentPractice, practiceId);
    return resolveInitialProgramTab(tabs, location.search);
  });
  const activeProgramTabRef = useRef(activeProgramTab);
  const analyticsRequestKeyRef = useRef('');
  useEffect(() => {
    activeProgramTabRef.current = activeProgramTab;
  }, [activeProgramTab]);
  const isSuperAdmin = Number(user?.role_id) === 1;
  const allowDeletePatient = !isReadOnly && canDeletePatient(user);
  const hasProgramTabs = programTabs.length > 0;
  // Super admins see the program pills tabs but NOT the analytics KPI panel
  const showAnalyticsPanel = hasProgramTabs && !isSuperAdmin;
  const showProgramPillTabs = hasProgramTabs; // always show program tabs regardless of role
  const showCptCodes = showAnalyticsPanel;
  const showClinicalUi =
    isDashboardView || [4, 5, 7].includes(Number(user?.role_id));
  const isEmbeddedDashboard =
    embeddedInPracticeDashboard || embeddedInProviderDashboard || embeddedInCaregiverDashboard;
  const useAdvancedPatientsStatRow = isEmbeddedDashboard;

  /** Preserve practice-dashboard ?tab= when updating ?program= (avoids bouncing back to Dashboard). */
  const updatePatientSearchParams = useCallback(
    (updates) => {
      if (dashboardTabKey && !isDashboardTabActive) {
        return;
      }
      const nextParams = new URLSearchParams(location.search);
      const urlTab = nextParams.get('tab');
      // If the user already navigated away (e.g. breadcrumb → Practice tab), do not
      // force tab=patient back — PatientManagement can still be mounted keep-alive.
      if (dashboardTabKey && urlTab && urlTab !== dashboardTabKey) {
        return;
      }
      if (dashboardTabKey) {
        nextParams.set('tab', dashboardTabKey);
      }
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          nextParams.delete(key);
        } else {
          nextParams.set(key, String(value));
        }
      });
      setSearchParams(nextParams, { replace: true });
    },
    [location.search, setSearchParams, dashboardTabKey, isDashboardTabActive]
  );

  useEffect(() => {
    if (searchParams.get(ADD_PATIENT_QUERY_FLAG) !== '1') return;

    setPatientToEdit(null);
    setShowAddPatientModal(true);
    updatePatientSearchParams({ [ADD_PATIENT_QUERY_FLAG]: null });
  }, [searchParams, updatePatientSearchParams]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const initialLoadTrackerRef = useRef({ practiceId: null, started: false });

  const listFetchParamsRef = useRef({
    currentPage: 1,
    entriesPerPage: 10,
    searchTerm: '',
    status: null,
    sortBy: 'name',
    sortOrder: 'asc',
    program: ''
  });

  useEffect(() => {
    listFetchParamsRef.current = {
      currentPage,
      entriesPerPage,
      searchTerm,
      status: localStatusFilter,
      sortBy,
      sortOrder,
      program: activeProgramTab
    };
  }, [currentPage, entriesPerPage, searchTerm, localStatusFilter, sortBy, sortOrder, activeProgramTab]);

  const enrolledProgramsKey = useMemo(() => {
    const list = currentPractice?.enrolled_programs;
    if (!Array.isArray(list) || list.length === 0) return '';
    return list
      .map((p) => (typeof p === 'string' ? p : (p.short_name || p.id || '')))
      .join('|');
  }, [currentPractice?.enrolled_programs]);

  const normalizeProgramKey = (name) => {
    if (!name) return '';
    return String(name).trim().toLowerCase().replace(/\s+/g, '-');
  };

  const formatProgramDate = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const selectedProgramBillingDates = useMemo(() => {
    if (!activeProgramTab || !Array.isArray(currentPractice?.enrolled_programs)) return null;
    const program = currentPractice.enrolled_programs.find((prog) => {
      const lookupName = typeof prog === 'string'
        ? prog
        : prog.short_name || prog.id || prog.full_name || prog.label || '';
      return normalizeProgramKey(lookupName) === String(activeProgramTab).trim().toLowerCase();
    });

    if (!program || typeof program === 'string') return null;

    const startDate = program.start_date || program.rpm_start_date || program.startDate || null;
    const endDate = program.end_date || program.rpm_end_date || program.endDate || null;
    if (!startDate && !endDate) return null;

    return {
      name: program.short_name || program.full_name || program.id || activeProgramTab,
      startDate,
      endDate
    };
  }, [activeProgramTab, currentPractice?.enrolled_programs]);

  useEffect(() => {
    if (!practiceId) return;

    const cachedTabs = getCachedProgramTabsForPractice(currentPractice, practiceId);
    if (cachedTabs.length > 0) {
      setProgramTabs((prev) => (programTabIdsKey(prev) === programTabIdsKey(cachedTabs) ? prev : cachedTabs));
      setActiveProgramTab((prev) => {
        const next = resolveInitialProgramTab(cachedTabs, location.search);
        return prev === next ? prev : next;
      });
      setProgramsReady(true);
      return undefined;
    }

    let cancelled = false;

    const loadPracticePrograms = async () => {
      let enrolled = [];
      try {
        const result = await dispatch(fetchPracticeSummary(practiceId));
        if (fetchPracticeSummary.fulfilled.match(result)) {
          enrolled = Array.isArray(result.payload?.data?.enrolled_programs)
            ? result.payload.data.enrolled_programs
            : [];
        }
      } catch (err) {
        console.error('Failed to load practice care programs:', err);
      }

      if (
        enrolled.length === 0 &&
        currentPractice &&
        String(currentPractice.id) === String(practiceId) &&
        Array.isArray(currentPractice.enrolled_programs)
      ) {
        enrolled = currentPractice.enrolled_programs;
      }

      if (cancelled) return;

      const nextTabs = buildProgramTabsWithCpt(enrolled);
      setProgramTabs((prev) => (programTabIdsKey(prev) === programTabIdsKey(nextTabs) ? prev : nextTabs));

      if (nextTabs.length > 0) {
        const nextTab = resolveInitialProgramTab(nextTabs, location.search);
        setActiveProgramTab((prev) => (prev === nextTab ? prev : nextTab));
      }

      setProgramsReady(true);
    };

    loadPracticePrograms();

    return () => {
      cancelled = true;
    };
  }, [practiceId, enrolledProgramsKey, dispatch, currentPractice, location.search]);

  const [practiceCaregivers, setPracticeCaregivers] = useState([]);
  const [practiceProviders, setPracticeProviders] = useState([]);
  const [hideReviewedPatients, setHideReviewedPatients] = useState(false);

  const handleAbnormalReviewStatusChange = useCallback((patientId, patch) => {
    if (patientId == null || !patch) return;
    const target = String(patientId);
    const matchId = (row) => {
      const candidates = [
        row?.id,
        row?.patient_id,
        row?.patient_table_id,
        row?.user_id,
        row?.patients_table_id
      ]
        .filter((v) => v != null && v !== '')
        .map((v) => String(v));
      return candidates.includes(target);
    };
    const closesReview = patch.abnormal_episode_pending === false || patch.is_reviewed === true;
    let decremented = false;
    setDashboardPatients((prev) => {
      if (!Array.isArray(prev)) return prev;
      let matched = false;
      const next = prev.map((row) => {
        if (!matchId(row)) return row;
        matched = true;
        const wasPending = row.abnormal_episode_pending === true || !(
          row.is_reviewed ||
          row.handled_by_name ||
          row.handled_at ||
          row.review_status === 'reviewed' ||
          row.abnormal_status === 'reviewed'
        );
        if (closesReview && wasPending) decremented = true;
        // Only apply review flags when the patch explicitly includes them
        // (Mark Reviewed). Invite/Assign must NOT mark the row as reviewed.
        return { ...row, ...patch };
      });
      if (!matched) {
        console.warn('[abnormal review] could not match patient row for status patch', patientId);
      }
      return next;
    });
    if (decremented) {
      setDashboardStats((stats) => {
        if (!stats?.summary || stats.summary.abnormal == null) return stats;
        return {
          ...stats,
          summary: {
            ...stats.summary,
            abnormal: Math.max(0, Number(stats.summary.abnormal) - 1)
          }
        };
      });
    }
  }, []);

  useEffect(() => {
    if (!practiceId) return;
    let cancelled = false;

    const fetchOptions = async () => {
      try {
        const [cgRes, prRes] = await Promise.all([
          practiceService.getPracticeCaregivers(practiceId),
          practiceService.getPracticeProviders(practiceId)
        ]);

        if (cancelled) return;

        if (cgRes.data?.success || Array.isArray(cgRes.data)) {
          const list = cgRes.data?.data || (Array.isArray(cgRes.data) ? cgRes.data : []);
          setPracticeCaregivers(list);
        }

        if (prRes.data?.success || Array.isArray(prRes.data)) {
          const list = prRes.data?.data || (Array.isArray(prRes.data) ? prRes.data : []);
          setPracticeProviders(list);
        }
      } catch (err) {
        console.warn('Failed to load practice caregivers/providers for pickers:', err.message);
      }
    };

    fetchOptions();

    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  useEffect(() => {
    if (!isDashboardTabActive) return;
    if (!programsReady || programTabs.length === 0) {
      if (programsReady && programTabs.length === 0) {
        setActiveProgramTab('');
      }
      return;
    }

    const params = new URLSearchParams(location.search);
    const urlProgram = String(params.get('program') || '').trim().toLowerCase();
    const matchedTab = programTabs.find((tab) => tab.id === urlProgram);

    if (matchedTab) {
      setActiveProgramTab((prev) => (prev === matchedTab.id ? prev : matchedTab.id));
      return;
    }

    const defaultTabId = resolveDefaultProgramTabId(programTabs);
    setActiveProgramTab(defaultTabId);
    if (!urlProgram) {
      updatePatientSearchParams({ program: defaultTabId });
    }
  }, [programsReady, programTabs, location.search, updatePatientSearchParams, isDashboardTabActive]);

  const handleProgramTabChange = (tabId) => {
    setActiveProgramTab(tabId);
    setDashboardPatients([]);
    setDashboardBucketTotal(0);
    setDashboardServerPaged(false);
    setActiveDashboardTable(null);
    setCurrentPage(1);
    setAnalyticsExpanded(true);
    if (sortBy === 'enrolled_status') {
      setSortBy('name');
    }
    updatePatientSearchParams({ program: tabId });
  };

  const handleAnalyticsFilter = (filterKey) => {
    setDashboardCurrentPage(1);
    setActiveDashboardTable((prev) => (prev === filterKey ? null : filterKey));
    // Status filter applies to Total Patients only
    setLocalStatusFilter(null);
    setCurrentPage(1);
  };

  const applyAnalyticsView = (filterKey) => {
    setDashboardCurrentPage(1);
    setActiveDashboardTable(filterKey);
    setLocalStatusFilter(null);
    setCurrentPage(1);
  };

  const handleCptBillingCodeClick = (tile) => {
    if (!tile?.bucketKey) return;
    setShowBillingModal(false);
    applyAnalyticsView(tile.bucketKey);
  };

  const clearAnalyticsFilter = () => {
    setActiveDashboardTable(null);
    setHideReviewedPatients(false);
    setDashboardCurrentPage(1);
    setCurrentPage(1);
  };

  const getNormalizedStatusId = (patient) => (
    normalizePatientStatusId(resolvePatientStatusRaw(patient))
  );

  const getStatusOrder = (patient) => getPatientStatusSortOrder(resolvePatientStatusRaw(patient));

  const getPatientSortValue = (patient, key) => {
    if (!patient) return '';
    switch (key) {
      case 'idx':
        return Number(resolvePatientRecordId(patient) || patient.id || 0);
      case 'status':
        return getStatusOrder(patient);
      case 'name':
        return `${String(patient.last_name || '').trim()}, ${String(patient.first_name || '').trim()}`
          .replace(/^,\s*|\s*,$/g, '')
          .toLowerCase();
      case 'systolic':
        return Number(patient.last_systolic || patient.systolic || 0);
      case 'diastolic':
        return Number(patient.last_diastolic || patient.diastolic || 0);
      case 'pulse':
        return Number(patient.last_pulse || patient.pulse || 0);
      case 'glucose':
        return Number(patient.last_glucose || patient.glucose || 0);
      case 'weight':
        return Number(patient.last_weight || patient.weight || 0);
      case 'last_upload':
        return patient.last_upload_date ? new Date(patient.last_upload_date).getTime() : 0;
      case 'readings':
        return Number(patient.readings_count || patient.number_of_readings || 0);
      case 'service_time':
        return Number(patient.service_time || 0);
      case 'dob':
        return patient.date_of_birth ? new Date(patient.date_of_birth).getTime() : 0;
      case 'chronic_conditions':
        return formatChronicDiseaseNames(patient.chronic_conditions || patient.icd_codes).toLowerCase();
      case 'last_activity':
        return patient.last_activity_date ? new Date(patient.last_activity_date).getTime() : 0;
      case 'billing':
        return String(patient.billing_status || 'pending').toLowerCase();
      case 'primary_diagnosis':
        return String(patient.primary_diagnosis || '').toLowerCase();
      case 'specialist':
        return String(patient.specialist_name || patient.specialist || '').toLowerCase();
      case 'monthly_time':
        return Number(patient.monthly_service_time || 0);
      case 'last_review':
        return patient.last_review_date ? new Date(patient.last_review_date).getTime() : 0;
      case 'system':
        return String(patient.rtm_system || patient.system || '').toLowerCase();
      case 'therapy_type':
        return String(patient.therapy_type || '').toLowerCase();
      case 'adherence':
        return Number(patient.adherence_rate || 0);
      case 'last_symptom':
        return patient.last_symptom_date ? new Date(patient.last_symptom_date).getTime() : 0;
      case 'alerts':
        return Number(patient.alert_count || 0);
      case 'procedure':
        return String(patient.cardiac_procedure || '').toLowerCase();
      case 'ecg_status':
        return String(patient.ecg_status || 'normal').toLowerCase();
      case 'avg_hr':
        return Number(patient.average_heart_rate || 0);
      case 'rehab_progress':
        return String(patient.rehab_progress || 'pending').toLowerCase();
      default:
        return String(patient[key] || '').toLowerCase();
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        setSearchTerm(localSearchTerm);
        setCurrentPage(1);
        setDashboardCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearchTerm, searchTerm]);

  const programTabsKey = programTabIdsKey(programTabs);
  const listFetchKey = `${practiceId}|${currentPage}|${resolvePatientListLimit(entriesPerPage)}|${searchTerm}|${localStatusFilter || ''}|${sortBy}|${sortOrder}|${activeProgramTab || ''}|${programsReady ? 1 : 0}|${programTabsKey}|${location.key}`;
  const listFetchKeyRef = useRef('');

  useEffect(() => {
    if (!practiceId) return;
    practiceService.invalidatePracticePatientCaches(practiceId);
  }, [practiceId, location.key]);

  // Fetch patients when parameters change (wait until program tab is resolved)
  useEffect(() => {
    if (!practiceId || !programsReady) return;
    if (programTabs.length > 0 && !activeProgramTab) return;
    if (listFetchKeyRef.current === listFetchKey) return;

    const resolvedProgram = activeProgramTab || resolveDefaultProgramTabId(programTabs) || 'rpm';

    const practiceChanged = initialLoadTrackerRef.current.practiceId !== practiceId;
    if (practiceChanged) {
      initialLoadTrackerRef.current = { practiceId, program: resolvedProgram, started: false };
      setInitialTableLoading(true);
    } else {
      initialLoadTrackerRef.current.program = resolvedProgram;
    }

    const params = {
      page: currentPage,
      limit: resolvePatientListLimit(entriesPerPage),
      search: searchTerm,
      status: localStatusFilter,
      sortBy,
      sortOrder,
      program: resolvedProgram
    };

    listFetchKeyRef.current = listFetchKey;
    initialLoadTrackerRef.current.started = true;
    dispatch(fetchPracticePatientsFilteredByProgramTab({ id: practiceId, params }));
  }, [listFetchKey, practiceId, programsReady, programTabs, activeProgramTab, currentPage, entriesPerPage, searchTerm, localStatusFilter, sortBy, sortOrder, dispatch]);

  // Refresh list + dashboard analytics when returning to this screen (e.g. after adding a follow-up)
  useEffect(() => {
    if (!practiceId || !activeProgramTab) return;
    setRefreshTrigger((prev) => prev + 1);
  }, [practiceId, activeProgramTab, location.key]);

  useEffect(() => {
    if (
      initialTableLoading &&
      initialLoadTrackerRef.current.started &&
      !patientsLoading
    ) {
      setInitialTableLoading(false);
    }
  }, [patientsLoading, initialTableLoading]);

  useEffect(() => {
    analyticsRequestKeyRef.current = '';
  }, [practiceId]);

  const reloadDashboardAnalytics = useCallback(async () => {
    if (!practiceId || !activeProgramTab || !hasProgramTabs || !showAnalyticsPanel) return;
    const requestKey = `${practiceId}:${activeProgramTab}:${Date.now()}`;
    analyticsRequestKeyRef.current = requestKey;
    try {
      setDashboardLoading(true);
      practiceService.invalidateProgramAnalyticsCache(practiceId);
      const response = await practiceService.getPracticeProgramPatientAnalytics(practiceId, {
        program: activeProgramTab,
        forceRefresh: true
      });
      if (analyticsRequestKeyRef.current === requestKey) {
        setDashboardStats(response?.data?.data || null);
      }
    } catch (err) {
      if (analyticsRequestKeyRef.current === requestKey) {
        setDashboardStats(null);
      }
    } finally {
      if (analyticsRequestKeyRef.current === requestKey) {
        setDashboardLoading(false);
      }
    }
  }, [practiceId, activeProgramTab, hasProgramTabs, showAnalyticsPanel]);

  useEffect(() => {
    let mounted = true;
    let timer = null;
    const loadDashboardAnalytics = async () => {
      // Superadmin hides the analytics panel — skip this heavy call so it doesn't contend with the list.
      if (!practiceId || !activeProgramTab || !hasProgramTabs || !showAnalyticsPanel) return;
      try {
        setDashboardLoading(true);
        const response = await practiceService.getPracticeProgramPatientAnalytics(practiceId, {
          program: activeProgramTab,
          forceRefresh: true
        });
        if (mounted) {
          setDashboardStats(response?.data?.data || null);
        }
      } catch (err) {
        if (mounted) setDashboardStats(null);
      } finally {
        if (mounted) setDashboardLoading(false);
      }
    };
    // Defer analytics so the patient list request gets DB priority first.
    timer = setTimeout(loadDashboardAnalytics, 200);
    return () => {
      mounted = false;
      if (timer) clearTimeout(timer);
    };
  }, [practiceId, activeProgramTab, hasProgramTabs, showAnalyticsPanel, refreshTrigger]);

  useEffect(() => {
    let mounted = true;
    const SERVER_DASHBOARD_FILTERS = new Set([
      'recentUploads',
      'missedUploads',
      'abnormalMeasurements',
      'cpt99453',
      'cpt99445',
      'cpt99454',
      'cpt99470',
      'cpt99457',
      'cpt99458',
      'activeCarePlan',
      'complexCcm',
      'itemsPendingReview'
    ]);
    const loadBucketPatients = async () => {
      if (!practiceId || !activeProgramTab || !activeDashboardTable || !hasProgramTabs) {
        if (mounted) {
          setDashboardPatients([]);
          setDashboardBucketTotal(0);
          setDashboardServerPaged(false);
          setBucketLoading(false);
        }
        return;
      }
      try {
        const keepsStableMainTable = isMainPatientTableColumnsFilter(activeDashboardTable);
        if (!keepsStableMainTable) {
          setDashboardPatients([]);
          setDashboardBucketTotal(0);
        }
        setBucketLoading(true);
        const useServerFilter = SERVER_DASHBOARD_FILTERS.has(activeDashboardTable);
        const pageLimit = resolvePatientListLimit(dashboardEntriesPerPage);
        const response = await practiceService.getPracticePatientsFilteredByProgramTab(practiceId, {
          page: useServerFilter ? dashboardCurrentPage : 1,
          limit: useServerFilter ? pageLimit : 500,
          program: activeProgramTab,
          search: searchTerm || '',
          ...(useServerFilter ? { dashboardFilter: activeDashboardTable } : {})
        });
        const rows = response?.data?.data?.patients || [];
        const total = Number(response?.data?.data?.pagination?.total);
        if (mounted) {
          setDashboardPatients(Array.isArray(rows) ? rows : []);
          setDashboardBucketTotal(Number.isFinite(total) ? total : (Array.isArray(rows) ? rows.length : 0));
          setDashboardServerPaged(useServerFilter);
        }
      } catch (err) {
        if (mounted) {
          setDashboardPatients([]);
          setDashboardBucketTotal(0);
          setDashboardServerPaged(false);
        }
      } finally {
        if (mounted) setBucketLoading(false);
      }
    };
    loadBucketPatients();
    return () => {
      mounted = false;
    };
  }, [
    practiceId,
    activeProgramTab,
    activeDashboardTable,
    hasProgramTabs,
    dashboardCurrentPage,
    dashboardEntriesPerPage,
    searchTerm
  ]);

  const handleSort = (key) => {
    const sortKey = key === 'enrolled_status' ? 'name' : key;
    if (sortBy === sortKey) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(sortKey);
    setSortOrder('asc');
  };

  useEffect(() => {
    if (sortBy === 'enrolled_status') {
      setSortBy('name');
    }
  }, [sortBy]);

  useEffect(() => {
    if (!error || patientsLoading || sortBy === 'name') return;
    setSortBy('name');
    setSortOrder('asc');
  }, [error, patientsLoading, sortBy]);

  // Socket.IO real-time updates for patient list (debounced to reduce DB load)
  useEffect(() => {
    if (!practiceId) return;

    socketService.connect();
    socketService.joinPracticeRoom(practiceId);

    let debounceTimer = null;
    const handlePatientVitalUpdate = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const params = {
          page: listFetchParamsRef.current.currentPage,
          limit: resolvePatientListLimit(listFetchParamsRef.current.entriesPerPage),
          search: listFetchParamsRef.current.searchTerm,
          status: listFetchParamsRef.current.status,
          sortBy: listFetchParamsRef.current.sortBy,
          sortOrder: listFetchParamsRef.current.sortOrder,
          program: listFetchParamsRef.current.program
        };
        practiceService.invalidatePracticePatientsCache(practiceId);
        dispatch(fetchPracticePatientsFilteredByProgramTab({ id: practiceId, params }));
      }, 10000);
    };

    socketService.onPatientVitalUpdate(handlePatientVitalUpdate);

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      socketService.offPatientVitalUpdate();
    };
  }, [practiceId, dispatch]);

  const totalPages = useMemo(
    () => computeTotalPages(pagination?.total, entriesPerPage),
    [pagination?.total, entriesPerPage]
  );
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleEntriesChange = (value) => {
    rememberPatientListPageSize(practiceId, value);
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handleAddPatient = () => {
    setShowAddPatientModal(true);
  };

  const patientStatusForBadge = (patient) => (
    patientStatusLabel(patient?.status ?? patient?.status_id ?? patient?.patient_status)
  );

  const handleViewPatient = (patient) => {
    const pid = resolvePatientRecordId(patient);
    if (pid == null) {
      console.warn('[PatientManagement] Cannot navigate: patient ID is null', patient);
      toast.error('Unable to open patient details: Missing ID');
      return;
    }

    if (!practiceId) {
      console.warn('[PatientManagement] Cannot navigate: practiceId is missing');
      toast.error('Unable to open patient details: Practice contextual missing');
      return;
    }

    console.info(`[PatientManagement] Navigating to detail: /practices/${practiceId}/patients/${pid}`);
    navigate(`/practices/${practiceId}/patients/${pid}`, { 
      state: { 
        fromDashboard: isFromPracticeDashboard(location),
        activeProgram: activeProgramTabRef.current
      } 
    });
  };

  const handleEditPatient = async (patient) => {
    if (!patient) return;
    const pid = resolvePatientRecordId(patient);
    if (pid == null) return;
    setEditLoading(pid);
    try {
      const resp = await patientService.getPatient(practiceId, pid);
      const fullPatient = resp?.data?.data || resp?.data || patient;
      setPatientToEdit(fullPatient);
      setShowAddPatientModal(true);
    } catch (err) {
      console.warn('[PatientManagement] Could not fetch full patient; using list row', err);
      setPatientToEdit(patient);
      setShowAddPatientModal(true);
    } finally {
      setEditLoading(null);
    }
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);

  const handleDeletePatient = (patient) => {
    setPatientToDelete(patient);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (patientToDelete) {
      const patientName = `${patientToDelete.first_name || ''} ${patientToDelete.last_name || ''}`.trim();
      const pid = resolvePatientRecordId(patientToDelete);

      if (pid == null) {
        toast.error('Cannot delete: missing patient id');
        setShowDeleteModal(false);
        setPatientToDelete(null);
        return;
      }

      try {
        await dispatch(deletePatient({ practiceId, patientId: pid })).unwrap();
        setShowDeleteModal(false);
        setPatientToDelete(null);
        toast.success(`Patient ${patientName} deleted successfully`);
        window.dispatchEvent(new CustomEvent('evitals:practice-patients-changed', {
          detail: { practiceId }
        }));

        // Refresh the list after deletion
        const params = {
          page: currentPage,
          limit: resolvePatientListLimit(entriesPerPage),
          search: searchTerm,
          status: localStatusFilter,
          sortBy,
          sortOrder,
          program: activeProgramTab
        };
        dispatch(fetchPracticePatientsFilteredByProgramTab({ id: practiceId, params }));
        practiceService.invalidatePracticePatientCaches(practiceId);
        setRefreshTrigger(prev => prev + 1);
        void reloadDashboardAnalytics();
      } catch (error) {
        setShowDeleteModal(false);
        setPatientToDelete(null);
        console.error('Error deleting patient:', error);
        const errorMessage = typeof error === 'string' ? error : (error.message || 'Failed to delete patient');
        toast.error(errorMessage);

        // Refresh the list to stay in sync
        const params = {
          page: currentPage,
          limit: resolvePatientListLimit(entriesPerPage),
          search: searchTerm,
          status: localStatusFilter,
          sortBy,
          sortOrder,
          program: activeProgramTab
        };
        dispatch(fetchPracticePatientsFilteredByProgramTab({ id: practiceId, params }));
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setPatientToDelete(null);
  };

  const pageNum = pagination?.current_page || currentPage || 1;
  const perPage = pagination?.per_page || resolvePatientListLimit(entriesPerPage);

  const patientTableStartIndex = useMemo(() => {
    if (entriesPerPage === 'all') return 0;
    return ((pageNum || 1) - 1) * (perPage || 10);
  }, [pageNum, perPage, entriesPerPage]);

  const patientIndexColumn = useMemo(
    () => ({
      key: 'idx',
      title: '#',
      sortable: false,
      className: 'reusable-table-centered-col',
      render: (_, rowIndex) => <span>{patientTableStartIndex + rowIndex + 1}</span>
    }),
    [patientTableStartIndex]
  );

  const withPatientIndexColumn = useCallback(
    (cols) =>
      isEmbeddedDashboard
        ? [patientIndexColumn, ...cols]
        : cols,
    [isEmbeddedDashboard, patientIndexColumn]
  );

  // RPM Columns
  const rpmColumns = useMemo(
    () => [
      {
        key: 'status',
        title: 'STATUS',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => <StatusBadge status={patientStatusForBadge(p)} />
      },
      {
        key: 'vitals',
        title: 'VITALS',
        sortable: true,
        className: 'reusable-table-centered-col patient-management-vitals-cell',
        render: (p) => {
          const vitals = String(p.vitals || '')
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
            .filter((v, idx, arr) => arr.indexOf(v) === idx);
          return vitals.length > 0 ? (
            <span className="patient-management-vitals-badges">
              {vitals.map((v, idx) => (
                <span key={idx} className="patient-management-vitals-badge">
                  {v}
                </span>
              ))}
            </span>
          ) : (
            <span className="patient-management-vitals-badge" style={{ opacity: 0.5 }}>
              -
            </span>
          );
        }
      },
      {
        key: 'name',
        title: 'NAME',
        sortable: true,
        render: (p) => (
          <a
            href="#"
            className="patient-management-name-link"
            onClick={(e) => {
              e.preventDefault();
              handleViewPatient(p);
            }}
          >
            {p.name || `${p.last_name || ''}, ${p.first_name || ''}`.replace(/^,\s*|\s*,$/g, '').trim() || 'N/A'}
          </a>
        )
      },
      {
        key: 'systolic',
        title: 'SYSTOLIC',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => {
          const raw = p.last_systolic ?? p.systolic;
          const num = (raw != null && raw !== 'N/A' && raw !== '') ? Number(raw) : null;
          const value = (num != null && !Number.isNaN(num) && num > 0) ? Math.round(num) : 'N/A';
          return renderVitalValue(value, getPatientListVitalStatuses(p).sysStatus);
        }
      },
      {
        key: 'diastolic',
        title: 'DIASTOLIC',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => {
          const raw = p.last_diastolic ?? p.diastolic;
          const num = (raw != null && raw !== 'N/A' && raw !== '') ? Number(raw) : null;
          const value = (num != null && !Number.isNaN(num) && num > 0) ? Math.round(num) : 'N/A';
          return renderVitalValue(value, getPatientListVitalStatuses(p).diaStatus);
        }
      },
      {
        key: 'pulse',
        title: 'PULSE',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => {
          const raw = p.last_pulse ?? p.pulse;
          const num = (raw != null && raw !== 'N/A' && raw !== '') ? Number(raw) : null;
          const value = (num != null && !Number.isNaN(num) && num > 0) ? Math.round(num) : 'N/A';
          return renderPulseValue(value, getPatientListVitalStatuses(p).pulseStatus);
        }
      },
      {
        key: 'glucose',
        title: 'GLUCOSE',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => {
          const raw = p.last_glucose ?? p.glucose;
          const num = (raw != null && raw !== 'N/A' && raw !== '') ? Number(raw) : null;
          const value = (num != null && !Number.isNaN(num) && num > 0) ? Math.round(num) : 'N/A';
          return renderVitalValue(value, getPatientListVitalStatuses(p).glucoseStatus);
        }
      },
      {
        key: 'weight',
        title: 'WEIGHT',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => {
          // DB stores kg; show lbs with one decimal (weight can be fractional)
          const val = p.last_weight ?? p.weight;
          const num = (val != null && val !== '' && val !== 'N/A') ? Number(val) : null;
          if (num == null || Number.isNaN(num) || num <= 0) {
            return renderVitalValue('N/A', 'normal');
          }
          const display = String(num);
          return renderVitalValue(display, getPatientListVitalStatuses(p).weightStatus);
        }
      },
      {
        key: 'last_upload',
        title: 'LAST UPLOAD',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => formatDateOnlyUs(p.last_upload_date) || 'N/A'
      },
      {
        key: 'readings',
        title: 'Readings',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => String(Number(p.readings_count ?? p.number_of_readings ?? 0) || 0)
      },
      {
        key: 'service_time',
        title: 'Service Time (min)',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.service_time || '0'
      },
      {
        key: 'act',
        title: 'ACTION',
        className: 'reusable-table-centered-col',
        render: (p) => (
          <ActionButtons
            onView={() => handleViewPatient(p)}
            onEdit={!isReadOnly ? () => handleEditPatient(p) : null}
            onDelete={allowDeletePatient ? () => handleDeletePatient(p) : null}
          />
        )
      }
    ],
    [practiceId, navigate, isReadOnly, allowDeletePatient, showClinicalUi]
  );
  const ccmColumns = useMemo(
    () => [
      {
        key: 'status',
        title: 'STATUS',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => <StatusBadge status={patientStatusForBadge(p)} />
      },
      {
        key: 'name',
        title: 'PATIENT NAME',
        sortable: true,
        render: (p) => (
          <a
            href="#"
            className="patient-management-name-link"
            onClick={(e) => {
              e.preventDefault();
              handleViewPatient(p);
            }}
          >
            {p.patient_name || `${p.last_name || ''}, ${p.first_name || ''}`.replace(/^,\s*|\s*,$/g, '').trim() || 'N/A'}
          </a>
        )
      },
      {
        key: 'dob',
        title: 'DOB',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.date_of_birth ? new Date(p.date_of_birth).toLocaleDateString() : 'N/A'
      },
      {
        key: 'chronic_conditions',
        title: 'CHRONIC CONDITIONS',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => formatChronicDiseaseNames(p.chronic_conditions || p.icd_codes)
      },
      {
        key: 'last_activity',
        title: 'LAST ACTIVITY',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.last_activity_date ? new Date(p.last_activity_date).toLocaleDateString() : 'N/A'
      },
      {
        key: 'service_time',
        title: 'SERVICE TIME (min)',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.service_time || '0'
      },
      {
        key: 'billing',
        title: 'BILLING',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.billing_status || 'Pending'
      },
      {
        key: 'act',
        title: 'ACTION',
        className: 'reusable-table-centered-col',
        render: (p) => (
          <ActionButtons
            onView={() => handleViewPatient(p)}
            onEdit={!isReadOnly ? () => handleEditPatient(p) : null}
            onDelete={allowDeletePatient ? () => handleDeletePatient(p) : null}
          />
        )
      }
    ],
    [practiceId, navigate, isReadOnly, allowDeletePatient]
  );

  // PCM Columns
  const pcmColumns = useMemo(
    () => [
      {
        key: 'status',
        title: 'STATUS',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => <StatusBadge status={patientStatusForBadge(p)} />
      },
      {
        key: 'name',
        title: 'PATIENT NAME',
        sortable: true,
        render: (p) => (
          <a
            href="#"
            className="patient-management-name-link"
            onClick={(e) => {
              e.preventDefault();
              handleViewPatient(p);
            }}
          >
            {p.patient_name || `${p.last_name || ''}, ${p.first_name || ''}`.replace(/^,\s*|\s*,$/g, '').trim() || 'N/A'}
          </a>
        )
      },
      {
        key: 'primary_diagnosis',
        title: 'PRIMARY DIAGNOSIS',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.primary_diagnosis || 'N/A'
      },
      {
        key: 'specialist',
        title: 'SPECIALIST',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.specialist_name || p.specialist || 'N/A'
      },
      {
        key: 'monthly_time',
        title: 'MONTHLY TIME (min)',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.monthly_service_time || '0'
      },
      {
        key: 'last_review',
        title: 'LAST REVIEW',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.last_review_date ? new Date(p.last_review_date).toLocaleDateString() : 'N/A'
      },
      {
        key: 'act',
        title: 'ACTION',
        className: 'reusable-table-centered-col',
        render: (p) => (
          <ActionButtons
            onView={() => handleViewPatient(p)}
            onEdit={!isReadOnly ? () => handleEditPatient(p) : null}
            onDelete={allowDeletePatient ? () => handleDeletePatient(p) : null}
          />
        )
      }
    ],
    [practiceId, navigate, isReadOnly, allowDeletePatient]
  );

  // RTM Columns
  const rtmColumns = useMemo(
    () => [
      {
        key: 'status',
        title: 'STATUS',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => <StatusBadge status={patientStatusForBadge(p)} />
      },
      {
        key: 'name',
        title: 'NAME',
        sortable: true,
        render: (p) => (
          <a
            href="#"
            className="patient-management-name-link"
            onClick={(e) => {
              e.preventDefault();
              handleViewPatient(p);
            }}
          >
            {p.name || `${p.last_name || ''}, ${p.first_name || ''}`.replace(/^,\s*|\s*,$/g, '').trim() || 'N/A'}
          </a>
        )
      },
      {
        key: 'system',
        title: 'SYSTEM',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.rtm_system || p.system || 'N/A'
      },
      {
        key: 'therapy_type',
        title: 'THERAPY TYPE',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.therapy_type || 'N/A'
      },
      {
        key: 'adherence',
        title: 'ADHERENCE',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.adherence_rate ? `${p.adherence_rate}%` : '0%'
      },
      {
        key: 'last_symptom',
        title: 'LAST SYMPTOM',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.last_symptom_date ? new Date(p.last_symptom_date).toLocaleDateString() : 'N/A'
      },
      {
        key: 'alerts',
        title: 'ALERTS',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.alert_count || '0'
      },
      {
        key: 'act',
        title: 'ACTION',
        className: 'reusable-table-centered-col',
        render: (p) => (
          <ActionButtons
            onView={() => handleViewPatient(p)}
            onEdit={!isReadOnly ? () => handleEditPatient(p) : null}
            onDelete={allowDeletePatient ? () => handleDeletePatient(p) : null}
          />
        )
      }
    ],
    [practiceId, navigate, isReadOnly, allowDeletePatient]
  );

  // Cardiac Columns
  const cardiacColumns = useMemo(
    () => [
      {
        key: 'status',
        title: 'STATUS',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => <StatusBadge status={patientStatusForBadge(p)} />
      },
      {
        key: 'name',
        title: 'PATIENT NAME',
        sortable: true,
        render: (p) => (
          <a
            href="#"
            className="patient-management-name-link"
            onClick={(e) => {
              e.preventDefault();
              handleViewPatient(p);
            }}
          >
            {p.patient_name || `${p.last_name || ''}, ${p.first_name || ''}`.replace(/^,\s*|\s*,$/g, '').trim() || 'N/A'}
          </a>
        )
      },
      {
        key: 'procedure',
        title: 'PROCEDURE',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.cardiac_procedure || p.procedure || 'N/A'
      },
      {
        key: 'ecg_status',
        title: 'ECG STATUS',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.ecg_status || 'Normal'
      },
      {
        key: 'avg_hr',
        title: 'AVG HR',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.average_heart_rate || p.avg_hr || 'N/A'
      },
      {
        key: 'alerts',
        title: 'ALERTS',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.alert_count || p.alerts || '0'
      },
      {
        key: 'rehab_progress',
        title: 'REHAB PROGRESS',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => p.rehab_progress || 'Pending'
      },
      {
        key: 'act',
        title: 'ACTION',
        className: 'reusable-table-centered-col',
        render: (p) => (
          <ActionButtons
            onView={() => handleViewPatient(p)}
            onEdit={!isReadOnly ? () => handleEditPatient(p) : null}
            onDelete={allowDeletePatient ? () => handleDeletePatient(p) : null}
          />
        )
      }
    ],
    [practiceId, navigate, isReadOnly, allowDeletePatient]
  );

  const currentPatientColumns = useMemo(() => {
    const programKey = String(activeProgramTab || 'rpm').toLowerCase();
    const useFixedWidths = isEmbeddedDashboard;

    let cols;
    switch (activeProgramTab) {
      case 'ccm':
        cols = withPatientIndexColumn(ccmColumns);
        break;
      case 'pcm':
        cols = withPatientIndexColumn(pcmColumns);
        break;
      case 'rtm':
        cols = withPatientIndexColumn(rtmColumns);
        break;
      case 'cardiac':
        cols = withPatientIndexColumn(cardiacColumns);
        break;
      default:
        cols = withPatientIndexColumn(rpmColumns);
    }
    return applyEmbeddedProgramColumnWidths(cols, programKey, useFixedWidths);
  }, [
    activeProgramTab,
    withPatientIndexColumn,
    ccmColumns,
    pcmColumns,
    rtmColumns,
    cardiacColumns,
    rpmColumns,
    isEmbeddedDashboard
  ]);

  const getPatientDisplayName = (p) =>
    p.patient_name || p.name || `${p.last_name || ''}, ${p.first_name || ''}`.replace(/^,\s*|\s*,$/g, '').trim() || 'N/A';

  const getPatientDob = (p) => {
    const raw = p?.date_of_birth || p?.dob;
    if (!raw) return 'N/A';
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString('en-US');
  };

  const getRecentUploadDate = (p) =>
    p?.last_vital_upload_at || p?.last_upload_date || p?.last_upload_at || p?.upload_date || null;

  const RECENT_UPLOAD_MS = 24 * 60 * 60 * 1000;
  const MISSED_UPLOAD_MS = 2 * 24 * 60 * 60 * 1000;

  const isRecentUpload = (p) => {
    if (p?.has_recent_upload === true) return true;
    if (p?.has_recent_upload === false && p?.last_vital_upload_at) return false;
    const uploadDate = getRecentUploadDate(p);
    if (!uploadDate) return false;
    const d = new Date(uploadDate);
    if (Number.isNaN(d.getTime())) return false;
    return Date.now() - d.getTime() <= RECENT_UPLOAD_MS;
  };

  const isMissedUpload = (p) => {
    if (p?.has_missed_upload === true) return true;
    if (p?.has_missed_upload === false) return false;
    const uploadDate = getRecentUploadDate(p);
    if (!uploadDate) return true;
    const d = new Date(uploadDate);
    if (Number.isNaN(d.getTime())) return true;
    return Date.now() - d.getTime() > MISSED_UPLOAD_MS;
  };

  const getHoursSinceLastUpload = (p) => {
    const uploadDate = getRecentUploadDate(p);
    if (!uploadDate) return null;
    const d = new Date(uploadDate);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60));
  };

  const isAbnormal = (p) =>
    Boolean(p?.has_abnormal_measurement) || Number(p?.abnormal_count || 0) > 0;

  const isActivePatient = (p) => getNormalizedStatusId(p) === 2;

  const hasActiveCarePlan = (p) => Boolean(p?.has_approved_care_plan);
  const isComplexCcmPatient = (p) => Boolean(p?.is_complex);
  const hasPendingReviewItems = (p) =>
    Boolean(p?.has_pending_review_items) || Number(p?.pending_review_count || 0) > 0;

  const formatCarePlanStatusLabel = (p) => {
    if (hasActiveCarePlan(p)) return 'Approved';
    const status = String(p?.care_plan_status || '').toLowerCase();
    if (status === 'draft') return 'Draft';
    if (status === 'active') return 'Unsigned';
    if (status) return status.charAt(0).toUpperCase() + status.slice(1);
    return 'Missing';
  };

  const formatComplexCcmLabel = (p) => (isComplexCcmPatient(p) ? 'Complex' : 'Standard');

  const getPatientCptCodes = (p) => {
    if (p?.billing_blocked) return [];
    if (Array.isArray(p?.cptCodes)) {
      return p.cptCodes.map((code) => String(code).trim()).filter(Boolean);
    }
    const raw = p?.eligible_cpt_codes;
    if (typeof raw === 'string' && raw && raw !== 'BLOCKED') {
      return raw.split(',').map((code) => code.trim()).filter(Boolean);
    }
    return [];
  };

  const patientEligibleForCpt = (p, code) =>
    getPatientCptCodes(p).includes(String(code));

  const buildCcmCptBucketFilters = (list) => {
    const codes = ['99490', '99439', '99491', '99437', '99487', '99489'];
    return codes.reduce((acc, code) => {
      acc[`cpt${code}`] = list.filter((p) => patientEligibleForCpt(p, code));
      return acc;
    }, {});
  };

  const isCptDashboardFilter = (filterKey) =>
    Boolean(filterKey && /^cpt\d+$/.test(String(filterKey)));

  const isMainPatientTableColumnsFilter = (filterKey) =>
    isCptDashboardFilter(filterKey) || RPM_UPLOAD_KPI_FILTERS.has(filterKey);

  const normalizePatientDate = (raw) => {
    if (!raw || raw === 'N/A') return null;
    const iso = String(raw).trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const isEligible99453 = (p) => {
    if (p?.eligible_99453 === true) return true;
    if (p?.eligible_99453 === false) return false;
    const from = dashboardStats?.billing_period?.date_from;
    const to = dashboardStats?.billing_period?.date_to;
    if (!from || !to) return false;
    const enrolledDay = normalizePatientDate(p?.enrolled_date || p?.created_at);
    if (!enrolledDay) return false;
    return enrolledDay >= from && enrolledDay <= to;
  };

  const get99458UnitCount = (minutes) => {
    const mins = Number(minutes) || 0;
    if (mins < 40) return 0;
    if (mins < 60) return 1;
    return 2;
  };

  const readingCount = (p) => Number(p?.readings_count || p?.number_of_readings || p?.total_readings || 0);
  const serviceMinutes = (p) => Number(p?.service_time || 0);
  // CPT click-through must use billing-period values (not lifetime readings / all-time service).
  const cptMeasurementDays = (p) => Number(p?.measurement_days ?? 0) || 0;
  const cptServiceMinutes = (p) => {
    if (p?.billing_period_service_time != null && p.billing_period_service_time !== '') {
      return Number(p.billing_period_service_time) || 0;
    }
    return serviceMinutes(p);
  };

  const dashboardBuckets = useMemo(() => {
    const list = Array.isArray(dashboardPatients) ? dashboardPatients : [];
    const programKey = String(activeProgramTab || '').toLowerCase();

    // Server already applied the KPI filter + page; keep rows as the active bucket.
    if (dashboardServerPaged && activeDashboardTable) {
      if (programKey === 'ccm') {
        return {
          activeCarePlan: [],
          complexCcm: [],
          itemsPendingReview: [],
          ...['99490', '99439', '99491', '99437', '99487', '99489'].reduce((acc, code) => {
            acc[`cpt${code}`] = [];
            return acc;
          }, {}),
          [activeDashboardTable]: list
        };
      }
      return {
        recentUploads: [],
        missedUploads: [],
        abnormalMeasurements: [],
        cpt99453: [],
        cpt99445: [],
        cpt99454: [],
        cpt99470: [],
        cpt99457: [],
        cpt99458: [],
        [activeDashboardTable]: list
      };
    }

    const activeOnly = list.filter(isActivePatient);

    if (programKey === 'ccm') {
      return {
        activeCarePlan: list.filter(hasActiveCarePlan),
        complexCcm: list.filter(isComplexCcmPatient),
        itemsPendingReview: list.filter(hasPendingReviewItems),
        ...buildCcmCptBucketFilters(activeOnly)
      };
    }

    return {
      recentUploads: activeOnly.filter((p) => isRecentUpload(p)),
      missedUploads: activeOnly.filter((p) => isMissedUpload(p)),
      abnormalMeasurements: activeOnly.filter((p) => isAbnormal(p)),
      cpt99453: activeOnly.filter((p) => isEligible99453(p)),
      cpt99445: activeOnly.filter((p) => {
        const days = cptMeasurementDays(p);
        return days >= 2 && days <= 15;
      }),
      cpt99454: activeOnly.filter((p) => cptMeasurementDays(p) >= 16),
      cpt99470: activeOnly.filter((p) => {
        const mins = cptServiceMinutes(p);
        return mins >= 10 && mins <= 19;
      }),
      cpt99457: activeOnly.filter((p) => cptServiceMinutes(p) >= 20),
      cpt99458: activeOnly.filter((p) => get99458UnitCount(cptServiceMinutes(p)) > 0)
    };
  }, [dashboardPatients, dashboardStats, activeProgramTab, dashboardServerPaged, activeDashboardTable]);

  const dashboardTableMap = useMemo(() => {
    const baseCols = [
      {
        key: 'status',
        title: 'Status',
        className: 'reusable-table-centered-col',
        render: (p) => <StatusBadge status={patientStatusForBadge(p)} />
      },
      { key: 'name', title: 'Name', render: (p) => getPatientDisplayName(p) },
      { key: 'dob', title: 'DOB', render: (p) => getPatientDob(p) },
      { key: 'phone', title: 'Phone', render: (p) => formatPhoneForDisplay(p.phone_number || p.phone || p.cell_phone_number, 'N/A') },
      { key: 'rpm_start', title: 'RPM Start Date', render: (p) => p.rpm_start_date || 'N/A' }
    ];

    const ccmDetailCols = [
      {
        key: 'status',
        title: 'Status',
        className: 'reusable-table-centered-col',
        render: (p) => <StatusBadge status={patientStatusForBadge(p)} />
      },
      {
        key: 'name',
        title: 'Patient Name',
        render: (p) => getPatientDisplayName(p)
      },
      {
        key: 'dob',
        title: 'DOB',
        render: (p) => getPatientDob(p)
      },
      {
        key: 'chronic_conditions',
        title: 'Chronic Conditions',
        render: (p) => formatChronicDiseaseNames(p.chronic_conditions || p.icd_codes)
      },
      {
        key: 'care_plan_status',
        title: 'Care Plan Status',
        className: 'reusable-table-centered-col',
        render: (p) => formatCarePlanStatusLabel(p)
      },
      {
        key: 'care_plan_name',
        title: 'Care Plan',
        render: (p) => p.care_plan_name || 'N/A'
      },
      {
        key: 'is_complex',
        title: 'CCM Type',
        className: 'reusable-table-centered-col',
        render: (p) => formatComplexCcmLabel(p)
      },
      {
        key: 'pending_review_count',
        title: 'Pending Review',
        className: 'reusable-table-centered-col',
        render: (p) => String(Number(p.pending_review_count || 0))
      },
      {
        key: 'service_time',
        title: 'Service Time (min)',
        className: 'reusable-table-centered-col',
        render: (p) => String(serviceMinutes(p))
      }
    ];

    const ccmCptTableEntries = ['99490', '99439', '99491', '99437', '99487', '99489'].reduce(
      (acc, code) => {
        const bucketKey = `cpt${code}`;
        acc[bucketKey] = {
          title: code,
          rows: dashboardBuckets[bucketKey] || [],
          columns: null
        };
        return acc;
      },
      {}
    );

    return {
      recentUploads: {
        title: 'Recent Uploads',
        rows: dashboardBuckets.recentUploads,
        columns: [
          ...baseCols,
          { key: 'measurement_date', title: 'Measurement Date', render: (p) => p.measurement_date || 'N/A' },
          { key: 'measurement_period', title: 'Measurement Period', render: (p) => p.measurement_period || 'N/A' },
          { key: 'data', title: 'Data', render: (p) => p.data_summary || p.vitals || 'N/A' },
          { key: 'upload_dt', title: 'Upload Date/Time', render: (p) => getRecentUploadDate(p) || 'N/A' },
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time (min)', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      missedUploads: {
        title: 'Missed Uploads',
        rows: dashboardBuckets.missedUploads,
        columns: [
          ...baseCols,
          { key: 'last_updated', title: 'Last Updated', render: (p) => p.updated_at || 'N/A' },
          { key: 'last_measurement', title: 'Last measurement date', render: (p) => p.last_measurement_date || getRecentUploadDate(p) || 'N/A' },
          {
            key: 'hours_missed',
            title: 'Days since last upload',
            render: (p) => {
              const hrs = getHoursSinceLastUpload(p);
              if (hrs == null) return 'Never';
              return String(Math.max(0, Math.floor(hrs / 24)));
            }
          },
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time (min)', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      abnormalMeasurements: {
        title: 'Abnormal Measurement',
        rows: dashboardBuckets.abnormalMeasurements,
        columns: [
          ...baseCols,
          { key: 'measurement_date', title: 'Measurement Date', render: (p) => p.measurement_date || p.last_measurement_date || 'N/A' },
          { key: 'measurement_period', title: 'Measurement Period', render: (p) => p.measurement_period || 'N/A' },
          { key: 'data', title: 'Data', render: (p) => p.data_summary || p.vitals || 'N/A' },
          { key: 'trend_7d', title: 'TREND (7D)', className: 'reusable-table-centered-col', render: (p) => <Trend7dCell patient={p} practiceId={practiceId} /> },
          { key: 'review', title: 'CAREGIVER REVIEW', className: 'reusable-table-centered-col patient-review-column-header', render: (p) => (
            <PatientReviewCell
              patient={p}
              practiceId={practiceId}
              caregiverOptions={practiceCaregivers}
              providerOptions={practiceProviders}
              isAbnormalContext
              onStatusChange={handleAbnormalReviewStatusChange}
            />
          ) },
          { key: 'upload_dt', title: 'Upload date/time', render: (p) => getRecentUploadDate(p) || 'N/A' },
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time (min)', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      cpt99453: {
        title: '99453',
        rows: dashboardBuckets.cpt99453,
        columns: [
          ...baseCols.slice(0, 1),
          { key: 'account', title: 'Account', render: (p) => String(p.patient_id || p.patient_table_id || p.id || 'N/A') },
          ...baseCols.slice(1),
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time (min)', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      cpt99445: {
        title: '99445',
        rows: dashboardBuckets.cpt99445,
        columns: [
          ...baseCols.slice(0, 1),
          { key: 'account', title: 'Account', render: (p) => String(p.patient_id || p.patient_table_id || p.id || 'N/A') },
          ...baseCols.slice(1),
          { key: 'enrolled', title: 'Enrolled Date', render: (p) => p.enrolled_date || 'N/A' },
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time (min)', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      cpt99454: {
        title: '99454',
        rows: dashboardBuckets.cpt99454,
        columns: [
          ...baseCols.slice(0, 1),
          { key: 'account', title: 'Account', render: (p) => String(p.patient_id || p.patient_table_id || p.id || 'N/A') },
          ...baseCols.slice(1),
          { key: 'enrolled', title: 'Enrolled Date', render: (p) => p.enrolled_date || 'N/A' },
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time (min)', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      cpt99470: {
        title: '99470',
        rows: dashboardBuckets.cpt99470,
        columns: [
          ...baseCols.slice(0, 1),
          { key: 'account', title: 'Account', render: (p) => String(p.patient_id || p.patient_table_id || p.id || 'N/A') },
          ...baseCols.slice(1),
          { key: 'enrolled', title: 'Enrolled Date', render: (p) => p.enrolled_date || 'N/A' },
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time (min)', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      cpt99457: {
        title: '99457',
        rows: dashboardBuckets.cpt99457,
        columns: [
          ...baseCols.slice(0, 1),
          { key: 'account', title: 'Account', render: (p) => String(p.patient_id || p.patient_table_id || p.id || 'N/A') },
          ...baseCols.slice(1),
          { key: 'enrolled', title: 'Enrolled Date', render: (p) => p.enrolled_date || 'N/A' },
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time (min)', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      cpt99458: {
        title: '99458',
        rows: dashboardBuckets.cpt99458,
        columns: [
          ...baseCols.slice(0, 1),
          { key: 'account', title: 'Account', render: (p) => String(p.patient_id || p.patient_table_id || p.id || 'N/A') },
          ...baseCols.slice(1),
          { key: 'enrolled', title: 'Enrolled Date', render: (p) => p.enrolled_date || 'N/A' },
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time (min)', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      activeCarePlan: {
        title: 'Active Care Plan',
        rows: dashboardBuckets.activeCarePlan || [],
        columns: ccmDetailCols
      },
      complexCcm: {
        title: 'Complex Care Plan',
        rows: dashboardBuckets.complexCcm || [],
        columns: ccmDetailCols
      },
      itemsPendingReview: {
        title: 'Items Pending Review',
        rows: dashboardBuckets.itemsPendingReview || [],
        columns: ccmDetailCols
      },
      ...ccmCptTableEntries
    };
  }, [
    dashboardBuckets,
    practiceId,
    practiceCaregivers,
    practiceProviders,
    handleAbnormalReviewStatusChange
  ]);

  const programSummary = useMemo(() => {
    const summary = dashboardStats?.summary || {};
    return {
      total: summary.total ?? 0,
      active: summary.active ?? 0,
      pending: summary.pending ?? 0,
      locked: summary.locked ?? 0,
      recent: summary.recent,
      missed: summary.missed,
      abnormal: summary.abnormal,
      activeCarePlan: summary.active_care_plan ?? 0,
      complexCcm: summary.complex_ccm ?? 0,
      itemsPendingReview: summary.items_pending_review ?? 0
    };
  }, [dashboardStats]);

  const resolvedProgramTab = activeProgramTab || resolveDefaultProgramTabId(programTabs) || 'rpm';
  const isCcmProgramTab = String(resolvedProgramTab).toLowerCase() === 'ccm';

  const formatKpiValue = (value) => (value == null ? '—' : value);

  const cptBucketCounts = useMemo(() => {
    const cpt = dashboardStats?.cpt || {};
    if (isCcmProgramTab) {
      return {
        cpt99490: cpt.cpt99490 ?? 0,
        cpt99439: cpt.cpt99439 ?? 0,
        cpt99491: cpt.cpt99491 ?? 0,
        cpt99437: cpt.cpt99437 ?? 0,
        cpt99487: cpt.cpt99487 ?? 0,
        cpt99489: cpt.cpt99489 ?? 0
      };
    }
    return {
      cpt99453: cpt.cpt99453 ?? 0,
      cpt99445: cpt.cpt99445 ?? 0,
      cpt99454: cpt.cpt99454 ?? 0,
      cpt99470: cpt.cpt99470 ?? 0,
      cpt99457: cpt.cpt99457 ?? 0,
      cpt99458: cpt.cpt99458 ?? 0
    };
  }, [dashboardStats, isCcmProgramTab]);

  const cptTiles = useMemo(() => {
    if (!showCptCodes || !hasProgramTabs || programTabs.length === 0) return [];

    const tabKey = resolvedProgramTab || resolveDefaultProgramTabId(programTabs) || '';
    const definitions = resolveProgramCptDefinitions(programTabs, tabKey);
    // Ineligible = active patients − eligible (pending/locked excluded from CPT cards).
    return buildCptTilesWithCounts(definitions, cptBucketCounts, programSummary.active);
  }, [
    showCptCodes,
    hasProgramTabs,
    programTabs,
    resolvedProgramTab,
    cptBucketCounts,
    programSummary.active
  ]);

  const showCptStatTile = showCptCodes && cptTiles.length > 0;

  useEffect(() => {
    setDashboardCurrentPage(1);
  }, [activeDashboardTable]);

  const patientsMatchActiveTab =
    !hasProgramTabs ||
    (String(practicePatientsPracticeId ?? '') === String(practiceId ?? '') &&
      String(practicePatientsProgram || '').toLowerCase() === String(resolvedProgramTab).toLowerCase());

  const displayPatients = useMemo(() => {
    if (!patientsMatchActiveTab) {
      return [];
    }
    const list = Array.isArray(practicePatients) ? practicePatients : [];
    if (sortBy !== 'service_time' && sortBy !== 'monthly_time') {
      return list;
    }
    const dir = sortOrder === 'desc' ? -1 : 1;
    const minutes = (p) => (
      sortBy === 'monthly_time'
        ? Number(p?.monthly_service_time || p?.monthly_time || 0) || 0
        : Number(p?.service_time || 0) || 0
    );
    return [...list].sort((a, b) => {
      const diff = minutes(a) - minutes(b);
      return diff === 0 ? 0 : diff * dir;
    });
  }, [patientsMatchActiveTab, practicePatients, sortBy, sortOrder]);

  const activeDashboardBucket = activeDashboardTable
    ? dashboardTableMap[activeDashboardTable]
    : null;

  const activeFilterLabel = activeDashboardBucket?.title
    || (activeDashboardTable ? String(activeDashboardTable) : '');

  const activeFilterSortHint =
    activeDashboardTable === 'recentUploads'
      ? 'Sorted by most uploads in the last 24 hours'
      : activeDashboardTable === 'abnormalMeasurements'
        ? 'Sorted by most abnormal readings in the last 24 hours'
        : activeDashboardTable === 'missedUploads'
          ? 'Sorted by longest time since last upload'
          : '';

  const isPatientReviewed = useCallback((p) => {
    if (!p) return false;
    if (p.abnormal_episode_pending === true) return false;
    return Boolean(
      p.is_reviewed ||
      p.handled_by_name ||
      p.handled_at ||
      p.review_status === 'reviewed' ||
      p.abnormal_status === 'reviewed'
    );
  }, []);

  const pendingAbnormalCount = useMemo(() => {
    const rows = dashboardBuckets.abnormalMeasurements || [];
    const pendingRows = rows.filter((p) => !isPatientReviewed(p)).length;
    const fullSetLoaded =
      rows.length > 0 &&
      (!dashboardServerPaged ||
        (activeDashboardTable === 'abnormalMeasurements' &&
          rows.length >= Number(dashboardBucketTotal || 0)));
    if (fullSetLoaded) return pendingRows;
    if (programSummary?.abnormal != null && Number(programSummary.abnormal) >= 0) {
      return Number(programSummary.abnormal);
    }
    return pendingRows;
  }, [
    activeDashboardTable,
    dashboardServerPaged,
    dashboardBucketTotal,
    programSummary?.abnormal,
    dashboardBuckets.abnormalMeasurements,
    isPatientReviewed
  ]);

  const activeFilterBannerEl = activeDashboardTable ? (
    <div className="ppa-active-filter-slot" aria-live="polite">
      <div
        className={`ppa-active-filter-banner${
          activeDashboardTable === 'abnormalMeasurements' ? ' ppa-active-filter-banner--abnormal' : ''
        }`}
        role="status"
      >
        <div className="ppa-active-filter-banner-text">
          {activeDashboardTable === 'abnormalMeasurements' ? (
            <span className="ppa-abnormal-banner-title">
              <span className="ppa-abnormal-warning-icon" aria-hidden>⚠️</span>
              <strong>
                {pendingAbnormalCount} {pendingAbnormalCount === 1 ? 'patient' : 'patients'} with abnormal readings need review
              </strong>
            </span>
          ) : (
            <>
              <span className="ppa-active-filter-label">Filter applied:</span>
              <span className="ppa-active-filter-value">{activeFilterLabel}</span>
              {RPM_UPLOAD_KPI_FILTERS.has(activeDashboardTable) ? (
                <span className="ppa-active-filter-hint">Active patients only</span>
              ) : null}
              {activeFilterSortHint ? (
                <span className="ppa-active-filter-hint">{activeFilterSortHint}</span>
              ) : null}
            </>
          )}
        </div>

        <div className="ppa-active-filter-banner-right">
          {activeDashboardTable === 'abnormalMeasurements' && (
            <label className="ppa-hide-reviewed-checkbox" title="Toggle to hide already reviewed patients">
              <input
                type="checkbox"
                checked={hideReviewedPatients}
                onChange={(e) => {
                  setHideReviewedPatients(e.target.checked);
                  setDashboardCurrentPage(1);
                }}
              />
              <span>Hide reviewed patients</span>
            </label>
          )}
          <button
            type="button"
            className="ppa-active-filter-clear"
            onClick={clearAnalyticsFilter}
          >
            Clear filter
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const dashboardFilteredRows = useMemo(() => {
    let rows = activeDashboardBucket?.rows || [];
    if (activeDashboardTable === 'abnormalMeasurements' && hideReviewedPatients) {
      rows = rows.filter((p) => !isPatientReviewed(p));
    }
    return rows;
  }, [activeDashboardBucket, activeDashboardTable, hideReviewedPatients, isPatientReviewed]);

  const dashboardTotalRecords = dashboardServerPaged
    ? (activeDashboardTable === 'abnormalMeasurements' && hideReviewedPatients
        ? pendingAbnormalCount
        : dashboardBucketTotal)
    : dashboardFilteredRows.length;

  const dashboardTotalPages = useMemo(
    () => computeTotalPages(dashboardTotalRecords, dashboardEntriesPerPage),
    [dashboardTotalRecords, dashboardEntriesPerPage]
  );

  const dashboardDisplayRows = useMemo(() => {
    if (!activeDashboardTable) return [];
    // Server already returned the current page for KPI filters.
    if (dashboardServerPaged) return dashboardFilteredRows;
    if (dashboardEntriesPerPage === 'all') return dashboardFilteredRows;
    const limit = parseInt(dashboardEntriesPerPage, 10) || 10;
    const startIndex = (dashboardCurrentPage - 1) * limit;
    return dashboardFilteredRows.slice(startIndex, startIndex + limit);
  }, [
    activeDashboardTable,
    dashboardFilteredRows,
    dashboardCurrentPage,
    dashboardEntriesPerPage,
    dashboardServerPaged
  ]);

  const mainTableIsLoading =
    !activeDashboardTable && (
      !patientsMatchActiveTab ||
      patientsLoading ||
      initialTableLoading
    );

  const dashboardUsesMainColumns = isMainPatientTableColumnsFilter(activeDashboardTable);

  const unifiedTableColumns = useMemo(() => {
    let cols;
    if (activeDashboardTable && activeDashboardBucket && !dashboardUsesMainColumns) {
      cols = activeDashboardBucket.columns;
    } else {
      cols = currentPatientColumns;
    }

    if (activeDashboardTable === 'abnormalMeasurements') {
      const hasTrendCol = cols.some((c) => c.key === 'trend_7d');
      const hasReviewCol = cols.some((c) => c.key === 'review');

      let nextCols = [...cols];
      if (!hasTrendCol) {
        const trendCol = {
          key: 'trend_7d',
          title: 'TREND (7D)',
          className: 'reusable-table-centered-col trend-7d-column-header',
          render: (p) => <Trend7dCell patient={p} practiceId={practiceId} />
        };
        const actIndex = nextCols.findIndex((c) => c.key === 'act');
        if (actIndex !== -1) {
          nextCols = [...nextCols.slice(0, actIndex), trendCol, ...nextCols.slice(actIndex)];
        } else {
          nextCols = [...nextCols, trendCol];
        }
      }

      if (!hasReviewCol) {
        const reviewCol = {
          key: 'review',
          title: 'CAREGIVER REVIEW',
          className: 'reusable-table-centered-col patient-review-column-header',
          render: (p) => (
            <PatientReviewCell
              patient={p}
              practiceId={practiceId}
              caregiverOptions={practiceCaregivers}
              providerOptions={practiceProviders}
              isAbnormalContext
              onStatusChange={handleAbnormalReviewStatusChange}
            />
          )
        };
        const actIndex = nextCols.findIndex((c) => c.key === 'act');
        if (actIndex !== -1) {
          nextCols = [...nextCols.slice(0, actIndex), reviewCol, ...nextCols.slice(actIndex)];
        } else {
          nextCols = [...nextCols, reviewCol];
        }
      }

      return nextCols;
    }

    return cols;
  }, [
    activeDashboardTable,
    activeDashboardBucket,
    dashboardUsesMainColumns,
    currentPatientColumns,
    practiceId,
    practiceCaregivers,
    practiceProviders,
    handleAbnormalReviewStatusChange
  ]);

  const unifiedTableData = useMemo(() => {
    let rows;
    if (!activeDashboardTable) {
      rows = displayPatients;
    } else if (dashboardDisplayRows.length > 0) {
      rows = dashboardDisplayRows;
    } else if (dashboardUsesMainColumns && bucketLoading && displayPatients.length > 0) {
      rows = displayPatients;
    } else {
      rows = dashboardDisplayRows;
    }

    if (
      rows === displayPatients ||
      !Array.isArray(rows) ||
      (sortBy !== 'service_time' && sortBy !== 'monthly_time')
    ) {
      return rows;
    }

    const dir = sortOrder === 'desc' ? -1 : 1;
    const minutes = (p) => (
      sortBy === 'monthly_time'
        ? Number(p?.monthly_service_time || p?.monthly_time || 0) || 0
        : Number(p?.service_time || 0) || 0
    );
    return [...rows].sort((a, b) => {
      const diff = minutes(a) - minutes(b);
      return diff === 0 ? 0 : diff * dir;
    });
  }, [
    activeDashboardTable,
    dashboardDisplayRows,
    dashboardUsesMainColumns,
    bucketLoading,
    displayPatients,
    sortBy,
    sortOrder
  ]);

  const unifiedTableIsLoading = activeDashboardTable
    ? bucketLoading && !(dashboardUsesMainColumns && unifiedTableData.length > 0)
    : mainTableIsLoading;

  const unifiedTableLoadingMode =
    activeDashboardTable && dashboardUsesMainColumns
      ? 'spinner'
      : (activeDashboardTable && bucketLoading) || unifiedTableData.length === 0
        ? 'skeleton'
        : 'spinner';

  const unifiedTableSkeletonRows = useMemo(() => {
    const raw = activeDashboardTable ? dashboardEntriesPerPage : entriesPerPage;
    if (raw === 'all') return 10;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 10;
  }, [activeDashboardTable, entriesPerPage, dashboardEntriesPerPage]);

  const unifiedEmptyMessage = activeDashboardTable
    ? 'No patients found for this category'
    : 'No patients found';

  const useErmpProviderPatientUi = true; // Always use the unified ERMP design (Image 1)
  const screenShellClass =
    isEmbeddedDashboard || useErmpProviderPatientUi
      ? 'pm-embedded-in-practice-dashboard'
      : 'pm-unified-white-card';

  const billingSummary = useMemo(() => {
    if (!cptTiles.length) {
      return { matches: 0, patients: 0, pct: 0 };
    }
    const matches = cptTiles.reduce((sum, tile) => sum + (tile.eligible ?? 0), 0);
    const patients = Number(programSummary.active) || 0;
    // Average eligibility across tracked codes only (codes with real count buckets).
    const tracked = cptTiles.filter((tile) => tile.bucketKey);
    const pctSource = tracked.length > 0 ? tracked : cptTiles;
    const pct =
      pctSource.length > 0
        ? Math.round(
            pctSource.reduce((sum, tile) => {
              const tileTotal = (tile.eligible ?? 0) + (tile.ineligible ?? 0);
              return sum + (tileTotal > 0 ? ((tile.eligible ?? 0) / tileTotal) * 100 : 0);
            }, 0) / pctSource.length
          )
        : 0;
    return { matches, patients, pct };
  }, [cptTiles, programSummary.active]);

  const cptPreviewTiles = cptTiles.slice(0, 3);
  const cptHiddenCount = Math.max(0, cptTiles.length - cptPreviewTiles.length);
  const billingRingCircumference = 144.5;
  const billingRingOffset = billingRingCircumference - (billingSummary.pct / 100) * billingRingCircumference;

  const programPillTabsEl =
    programTabs.length > 0 ? (
      <div className="ppa-analytics-tabs" role="tablist">
        {programTabs.map((tab) => (
          <button
            key={tab.id}
            className={`ppa-pill-btn ${activeProgramTab === tab.id ? 'active' : ''}`}
            onClick={() => handleProgramTabChange(tab.id)}
            type="button"
            role="tab"
            aria-selected={activeProgramTab === tab.id}
          >
            <FontAwesomeIcon icon={getProgramTabIcon(tab)} className="ppa-tab-icon" />
            <span title={tab.displayName || tab.full_name || tab.label}>{tab.label}</span>
          </button>
        ))}
      </div>
    ) : null;

  const analyticsExpandBtn = showAnalyticsPanel ? (
    <button
      type="button"
      className="ppa-expand-toggle-btn"
      onClick={() => setAnalyticsExpanded((prev) => !prev)}
      aria-label={analyticsExpanded ? 'Collapse analytics' : 'Expand analytics'}
    >
      <FontAwesomeIcon icon={analyticsExpanded ? faChevronUp : faChevronDown} />
    </button>
  ) : null;

  const legacyAnalyticsKpiPanel =
    showAnalyticsPanel && analyticsExpanded ? (
      <div className={`ppa-analytics-wrap ppa-analytics-wrap--collapsible ${useErmpProviderPatientUi ? '' : 'pm-patient-stats-panel'}`}>
        <div className="ppa-analytics-section-header ppa-analytics-section-header--in-wrap">
          {analyticsExpandBtn}
        </div>
        <div className="ppa-kpi-row">
          <div
            className={`ppa-kpi-card${!activeDashboardTable ? ' ppa-kpi-card--active' : ''}`}
            onClick={clearAnalyticsFilter}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && clearAnalyticsFilter()}
          >
            <div className="ppa-kpi-label">Total Patient</div>
            <div className="ppa-kpi-content">
              <div className="ppa-kpi-value ppa-val-blue">{programSummary.total ?? 0}</div>
              <div className="ppa-kpi-pills">
                <div
                  className="ppa-status-pill ppa-pill-active"
                  aria-label="Active"
                  data-tooltip="Active"
                >
                  <span className="ppa-pill-letter">A</span>
                  <span>{programSummary.active ?? 0}</span>
                </div>
                <div
                  className="ppa-status-pill ppa-pill-pending"
                  aria-label="Pending"
                  data-tooltip="Pending"
                >
                  <span className="ppa-pill-letter">P</span>
                  <span>{programSummary.pending ?? 0}</span>
                </div>
                <div
                  className="ppa-status-pill ppa-pill-locked"
                  aria-label="Locked"
                  data-tooltip="Locked"
                >
                  <span className="ppa-pill-letter">L</span>
                  <span>{programSummary.locked ?? 0}</span>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`ppa-kpi-card${activeDashboardTable === 'recentUploads' ? ' ppa-kpi-card--active' : ''}`}
            onClick={() => handleAnalyticsFilter('recentUploads')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleAnalyticsFilter('recentUploads')}
          >
            <div className="ppa-kpi-label">Recent Uploads</div>
            <div className="ppa-kpi-content">
              <div className="ppa-kpi-value ppa-val-green">{formatKpiValue(programSummary.recent)}</div>
              <div className="ppa-kpi-sub">in last 24 hours</div>
            </div>
          </div>

          <div
            className={`ppa-kpi-card${activeDashboardTable === 'missedUploads' ? ' ppa-kpi-card--active' : ''}`}
            onClick={() => handleAnalyticsFilter('missedUploads')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleAnalyticsFilter('missedUploads')}
          >
            <div className="ppa-kpi-label">Missed Uploads</div>
            <div className="ppa-kpi-content">
              <div className="ppa-kpi-value ppa-val-gray">{formatKpiValue(programSummary.missed)}</div>
              <div className="ppa-kpi-sub">more than 2 days</div>
            </div>
          </div>

          <div
            className={`ppa-kpi-card${activeDashboardTable === 'abnormalMeasurements' ? ' ppa-kpi-card--active' : ''}`}
            onClick={() => handleAnalyticsFilter('abnormalMeasurements')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleAnalyticsFilter('abnormalMeasurements')}
          >
            <div className="ppa-kpi-label">Abnormal Readings</div>
            <div className="ppa-kpi-content">
              <div className="ppa-kpi-value ppa-val-amber">{formatKpiValue(pendingAbnormalCount)}</div>
              <div className="ppa-kpi-sub">in last 24 hours</div>
            </div>
          </div>
        </div>

        {showCptCodes && cptTiles.length > 0 && (
          <div className="ppa-cpt-tile-grid">
            {cptTiles.map((tile) => (
              <div
                key={tile.code}
                className={`ppa-cpt-tile${activeDashboardTable === tile.bucketKey ? ' ppa-cpt-tile--active' : ''}`}
                title={tile.desc}
                onClick={() => tile.bucketKey && handleAnalyticsFilter(tile.bucketKey)}
                role={tile.bucketKey ? 'button' : undefined}
                tabIndex={tile.bucketKey ? 0 : undefined}
                onKeyDown={(e) => tile.bucketKey && e.key === 'Enter' && handleAnalyticsFilter(tile.bucketKey)}
              >
                <span className="ppa-cpt-code-pill">{tile.code}</span>
                <div className="ppa-cpt-tile-counts">
                  <div className="ppa-cpt-tile-count-col">
                    <span className="ppa-cpt-tile-count ppa-val-green">{tile.eligible ?? 0}</span>
                    <span className="ppa-cpt-tile-count-label">Elig</span>
                  </div>
                  <div className="ppa-cpt-tile-count-col">
                    <span className="ppa-cpt-tile-count ppa-val-red">{tile.ineligible ?? 0}</span>
                    <span className="ppa-cpt-tile-count-label">Inelig</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    ) : null;

  const advancedAnalyticsKpiPanel =
    showAnalyticsPanel ? (
      isCcmProgramTab ? (
        <div className={`ppa-stat-row${showCptStatTile ? '' : ' ppa-stat-row--four-cols'}`}>
          <div className={`ppa-stat-tile${!activeDashboardTable ? ' ppa-stat-tile--active' : ''}`}>
            <p className="ppa-stat-label">Total Patients</p>
            <p className="ppa-stat-num">{programSummary.total ?? 0}</p>
            <div className="ppa-stat-badges">
              <span
                className="ppa-stat-badge ppa-stat-badge-a"
                aria-label="Active"
                data-tooltip="Active"
              >
                A {programSummary.active ?? 0}
              </span>
              <span
                className="ppa-stat-badge ppa-stat-badge-p"
                aria-label="Pending"
                data-tooltip="Pending"
              >
                P {programSummary.pending ?? 0}
              </span>
              <span
                className="ppa-stat-badge ppa-stat-badge-l"
                aria-label="Locked"
                data-tooltip="Locked"
              >
                L {programSummary.locked ?? 0}
              </span>
            </div>
            <button
              type="button"
              className="ppa-stat-view-all"
              onClick={clearAnalyticsFilter}
            >
              click to view all
            </button>
          </div>

          <div className={`ppa-stat-tile${activeDashboardTable === 'activeCarePlan' ? ' ppa-stat-tile--active' : ''}`}>
            <p className="ppa-stat-label">Active Care Plan</p>
            <p className="ppa-stat-num ppa-stat-num-green">{programSummary.activeCarePlan ?? 0}</p>
            <p className="ppa-stat-sub">provider approved</p>
            <button
              type="button"
              className="ppa-stat-view-all"
              onClick={() => applyAnalyticsView('activeCarePlan')}
            >
              click to view all
            </button>
          </div>

          <div className={`ppa-stat-tile${activeDashboardTable === 'complexCcm' ? ' ppa-stat-tile--active' : ''}`}>
            <p className="ppa-stat-label">Complex Care Plan</p>
            <p className="ppa-stat-num">{programSummary.complexCcm ?? 0}</p>
            <p className="ppa-stat-sub">attested complex CCM</p>
            <button
              type="button"
              className="ppa-stat-view-all"
              onClick={() => applyAnalyticsView('complexCcm')}
            >
              click to view all
            </button>
          </div>

          <div className={`ppa-stat-tile${activeDashboardTable === 'itemsPendingReview' ? ' ppa-stat-tile--active' : ''}`}>
            <p className="ppa-stat-label">Items Pending Review</p>
            <p className="ppa-stat-num ppa-stat-num-amber">{programSummary.itemsPendingReview ?? 0}</p>
            <p className="ppa-stat-sub">care plan changes awaiting provider</p>
            <button
              type="button"
              className="ppa-stat-view-all"
              onClick={() => applyAnalyticsView('itemsPendingReview')}
            >
              click to view all
            </button>
          </div>

          {showCptStatTile ? (
            <div className="ppa-stat-tile ppa-cpt-stat-tile">
              <div className="ppa-cpt-stat-head">
                <p className="ppa-stat-label">CPT Billing</p>
              </div>
              <div className="ppa-cpt-stat-list">
                {cptPreviewTiles.map((tile) => (
                  <div key={tile.code} className="ppa-cpt-stat-row-item">
                    <span className="ppa-cpt-stat-code">{tile.code}</span>
                    <span className="ppa-cpt-stat-chips">
                      <span className="ppa-cpt-mini-chip ppa-cpt-mini-chip-elig">{tile.eligible ?? 0}</span>
                      <span className="ppa-cpt-mini-chip ppa-cpt-mini-chip-inelig">{tile.ineligible ?? 0}</span>
                    </span>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="ppa-stat-view-all"
                onClick={() => setShowBillingModal(true)}
              >
                {cptHiddenCount > 0
                  ? `+${cptHiddenCount} more · click to view all`
                  : 'click to view all'}
              </button>
            </div>
          ) : null}
        </div>
      ) : (
      <>
        <div className={`ppa-stat-row${showCptStatTile ? '' : ' ppa-stat-row--four-cols'}`}>
          <div className={`ppa-stat-tile${!activeDashboardTable ? ' ppa-stat-tile--active' : ''}`}>
            <p className="ppa-stat-label">Total Patient</p>
            <p className="ppa-stat-num">{programSummary.total ?? 0}</p>
            <div className="ppa-stat-badges">
              <span
                className="ppa-stat-badge ppa-stat-badge-a"
                aria-label="Active"
                data-tooltip="Active"
              >
                A {programSummary.active ?? 0}
              </span>
              <span
                className="ppa-stat-badge ppa-stat-badge-p"
                aria-label="Pending"
                data-tooltip="Pending"
              >
                P {programSummary.pending ?? 0}
              </span>
              <span
                className="ppa-stat-badge ppa-stat-badge-l"
                aria-label="Locked"
                data-tooltip="Locked"
              >
                L {programSummary.locked ?? 0}
              </span>
            </div>
            <button
              type="button"
              className="ppa-stat-view-all"
              onClick={clearAnalyticsFilter}
            >
              click to view all
            </button>
          </div>

          <div className={`ppa-stat-tile${activeDashboardTable === 'recentUploads' ? ' ppa-stat-tile--active' : ''}`}>
            <p className="ppa-stat-label">Recent Uploads</p>
            <p className="ppa-stat-num ppa-stat-num-green">{formatKpiValue(programSummary.recent)}</p>
            <p className="ppa-stat-sub">in last 24 hours</p>
            <button
              type="button"
              className="ppa-stat-view-all"
              onClick={() => applyAnalyticsView('recentUploads')}
            >
              click to view all
            </button>
          </div>

          <div className={`ppa-stat-tile${activeDashboardTable === 'missedUploads' ? ' ppa-stat-tile--active' : ''}`}>
            <p className="ppa-stat-label">Missed Uploads</p>
            <p className="ppa-stat-num">{formatKpiValue(programSummary.missed)}</p>
            <p className="ppa-stat-sub">more than 2 days</p>
            <button
              type="button"
              className="ppa-stat-view-all"
              onClick={() => applyAnalyticsView('missedUploads')}
            >
              click to view all
            </button>
          </div>

          <div className={`ppa-stat-tile${activeDashboardTable === 'abnormalMeasurements' ? ' ppa-stat-tile--active' : ''}`}>
            <p className="ppa-stat-label">Abnormal Readings</p>
            <p className="ppa-stat-num ppa-stat-num-amber">{formatKpiValue(pendingAbnormalCount)}</p>
            <p className="ppa-stat-sub">in last 24 hours</p>
            <button
              type="button"
              className="ppa-stat-view-all"
              onClick={() => applyAnalyticsView('abnormalMeasurements')}
            >
              click to view all
            </button>
          </div>

          {showCptStatTile ? (
            <div className="ppa-stat-tile ppa-cpt-stat-tile">
              <div className="ppa-cpt-stat-head">
                <p className="ppa-stat-label">CPT Billing</p>
              </div>
              <div className="ppa-cpt-stat-list">
                {cptPreviewTiles.map((tile) => (
                  <div key={tile.code} className="ppa-cpt-stat-row-item">
                    <span className="ppa-cpt-stat-code">{tile.code}</span>
                    <span className="ppa-cpt-stat-chips">
                      <span className="ppa-cpt-mini-chip ppa-cpt-mini-chip-elig">{tile.eligible ?? 0}</span>
                      <span className="ppa-cpt-mini-chip ppa-cpt-mini-chip-inelig">{tile.ineligible ?? 0}</span>
                    </span>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="ppa-stat-view-all"
                onClick={() => setShowBillingModal(true)}
              >
                {cptHiddenCount > 0
                  ? `+${cptHiddenCount} more · click to view all`
                  : 'click to view all'}
              </button>
            </div>
          ) : null}
        </div>
      </>
      )
    ) : null;

  const analyticsKpiPanel = useAdvancedPatientsStatRow
    ? advancedAnalyticsKpiPanel
    : legacyAnalyticsKpiPanel;

  const isStatusFilterLockedToActive = RPM_UPLOAD_KPI_FILTERS.has(activeDashboardTable);

  const patientStatusFilterEl = (
    <div className={embeddedInPracticeDashboard ? 'pm-filter-group' : 'patient-management-filter-group'}>
      <select
        className={embeddedInPracticeDashboard ? 'pm-status-select' : 'patient-management-status-select'}
        value={isStatusFilterLockedToActive ? 2 : (localStatusFilter ?? '')}
        disabled={isStatusFilterLockedToActive}
        onChange={(e) => {
          const next = e.target.value;
          setLocalStatusFilter(next === '' ? null : Number(next));
          setCurrentPage(1);
        }}
        aria-label={
          isStatusFilterLockedToActive
            ? 'Patient status is fixed to Active for this view'
            : 'Filter patients by status'
        }
      >
        <option value="">All Status</option>
        <option value="2">Active</option>
        <option value="3">Pending</option>
        <option value="4">Locked</option>
      </select>
    </div>
  );

  const patientSearchEl = (
    <SearchBar
      value={localSearchTerm}
      onChange={setLocalSearchTerm}
      placeholder="Search patients by name..."
      label={null}
      className={
        embeddedInPracticeDashboard
          ? 'pm-search-wrapper'
          : useErmpProviderPatientUi
            ? 'pd-search-bar-inner'
            : 'patient-management-search-wrapper'
      }
      inputClassName={useErmpProviderPatientUi && !embeddedInPracticeDashboard ? 'pd-search-input' : undefined}
    />
  );

  const patientProgramTabsEl =
    showProgramPillTabs && programPillTabsEl ? (
      <div className="pd-filter-program-tabs">{programPillTabsEl}</div>
    ) : null;

  const patientAddBtnEl =
    !isReadOnly ? (
      <button
        type="button"
        className={
          embeddedInPracticeDashboard
            ? 'pm-add-btn'
            : useErmpProviderPatientUi
              ? 'pd-add-btn'
              : 'patient-management-add-btn'
        }
        onClick={handleAddPatient}
      >
        <FontAwesomeIcon
          icon={faPlus}
          className={embeddedInPracticeDashboard ? 'pm-add-icon' : 'patient-management-add-icon'}
        />
        Add Patient
      </button>
    ) : null;

  const collapsedAnalyticsToggleEl =
    showAnalyticsPanel && !analyticsExpanded && !useAdvancedPatientsStatRow ? analyticsExpandBtn : null;

  const patientFilterBarEl = embeddedInPracticeDashboard ? (
    <div className="pm-header-row">
      <div className="pm-header-actions">
        {patientSearchEl}
        {patientStatusFilterEl}
        {patientProgramTabsEl}
        {collapsedAnalyticsToggleEl}
        {patientAddBtnEl}
      </div>
    </div>
  ) : useErmpProviderPatientUi ? (
    <div className="pd-filter-bar pd-filter-bar--with-programs">
      <div className="pd-search-wrap">{patientSearchEl}</div>
      {patientStatusFilterEl}
      {patientProgramTabsEl}
      {collapsedAnalyticsToggleEl}
      {patientAddBtnEl}
    </div>
  ) : (
    <div className="patient-management-header-row patient-management-header-row--search-only">
      <div className="patient-management-header-actions">
        {patientSearchEl}
        {patientStatusFilterEl}
        {patientProgramTabsEl}
        {/* {selectedProgramBillingDates && (
          <div className="patient-management-billing-date">
            <span className="patient-management-billing-label">Billing Date</span>
            <span className="patient-management-billing-value">
              {selectedProgramBillingDates.name}: {formatProgramDate(selectedProgramBillingDates.startDate)} - {formatProgramDate(selectedProgramBillingDates.endDate)}
            </span>
          </div>
        )} */}
        {collapsedAnalyticsToggleEl}
        {patientAddBtnEl}
      </div>
    </div>
  );

  const patientTableEl = (
    <div
      id="dashboard-detail-table"
      className={`pm-dashboard-detail-table${
        isEmbeddedDashboard &&
        (!activeDashboardTable || dashboardUsesMainColumns)
          ? ' pm-rpm-stable-table'
          : ''
      }`}
    >
      <Table
        columns={unifiedTableColumns}
        data={unifiedTableData}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        isLoading={unifiedTableIsLoading}
        loadingMode={unifiedTableLoadingMode}
        skeletonRows={unifiedTableSkeletonRows}
        emptyMessage={unifiedEmptyMessage}
      />
    </div>
  );

  const dashboardFooterEl = (
    <ListScreenPaginationBar
      entriesValue={dashboardEntriesPerPage}
      onEntriesChange={(value) => {
        setDashboardEntriesPerPage(value);
        setDashboardCurrentPage(1);
      }}
      showPageSelector={dashboardEntriesPerPage !== 'all'}
      currentPage={dashboardCurrentPage}
      totalRecords={dashboardTotalRecords}
      totalPages={dashboardTotalPages}
      onPageChange={setDashboardCurrentPage}
    />
  );

  const patientFooterEl = (
    <ListScreenPaginationBar
      entriesValue={entriesPerPage === 'all' || entriesPerPage === 'All' ? 'all' : String(entriesPerPage)}
      onEntriesChange={handleEntriesChange}
      currentPage={currentPage}
      totalRecords={pagination?.total}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      leftSlot={!hideEventLogsButton && (
        <button
          type="button"
          className={
            useErmpProviderPatientUi
              ? 'pd-logs-btn patient-management-events-log-btn'
              : 'patient-management-events-log-btn'
          }
          onClick={() => {
            if (typeof onOpenEventLogs === 'function') {
              onOpenEventLogs();
              return;
            }
            navigate(appendFromDashboardQuery(`/event-logs/Patient/all?practiceId=${practiceId}`, location));
          }}
        >
          <FontAwesomeIcon icon={faList} className="patient-management-add-icon" />
          Event Logs
        </button>
      )}
    />
  );

  const patientTableSectionEl = (
    <>
      {patientTableEl}
      {activeDashboardTable ? dashboardFooterEl : patientFooterEl}
    </>
  );

  const patientScreenContent = useErmpProviderPatientUi ? (
    <div className="pm-ermp-patient-hub">
      <div className={`pm-tab-content-area ${showAnalyticsPanel ? 'pm-tab-content-with-analytics' : ''}${showAnalyticsPanel && !analyticsExpanded && !useAdvancedPatientsStatRow ? ' pm-tab-content-analytics-collapsed' : ''}`}>
        {analyticsKpiPanel}
        {activeFilterBannerEl}
        {patientFilterBarEl}
        <div className="practice-tab-table-card">
          {isEmbeddedDashboard ? (
            patientTableSectionEl
          ) : (
            <>
              <div className="pd-table-wrapper">
                <div className="patient-management-table-container">
                  {patientTableEl}
                </div>
              </div>
              <div className="pd-footer pm-patient-screen-footer">
                {activeDashboardTable ? dashboardFooterEl : patientFooterEl}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  ) : (
    <>
      <div className={`pm-tab-content-area ${showAnalyticsPanel ? 'pm-tab-content-with-analytics' : ''}${showAnalyticsPanel && !analyticsExpanded ? ' pm-tab-content-analytics-collapsed' : ''}`}>
        {showAnalyticsPanel ? (
          <div className="pm-analytics-subsection">
            {analyticsKpiPanel}
            {activeFilterBannerEl}
          </div>
        ) : null}

        <div className="pm-patient-list-frame">
          <div className="pm-patient-list-panel">
            <div className="pm-patient-screen-body">
              {patientFilterBarEl}
              <div className="practice-tab-table-card">
                <div className="patient-management-table-container">
                  {patientTableEl}
                </div>
                <div className="pm-patient-screen-footer">
                  {activeDashboardTable ? dashboardFooterEl : patientFooterEl}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div
      className={`patient-management-container${isEmbeddedDashboard ? ' patient-management-container--embedded' : ''}${useErmpProviderPatientUi ? ' patient-management-container--ermp-c' : ''}`}
    >
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {formatDisplayError(error)}
        </div>
      )}

      <div className={screenShellClass}>{patientScreenContent}</div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Delete</h5>
              <button type="button" className="modal-close" onClick={cancelDelete}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the patient <strong>{patientToDelete ? `${patientToDelete.first_name} ${patientToDelete.last_name}` : ''}</strong>?</p>
              <p className="modal-warning">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={cancelDelete}>Cancel</button>
              <button type="button" className="btn-delete" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* CPT Billing Readiness Modal */}
      {showBillingModal && showCptCodes && cptTiles.length > 0 && (
        <div
          className="modal-overlay ppa-billing-modal-overlay"
          role="presentation"
        >
          <div
            className="modal-container ppa-billing-modal-container"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ppa-billing-modal-title"
          >
            <div className="modal-header">
              <h5 className="modal-title" id="ppa-billing-modal-title">CPT Billing Readiness</h5>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowBillingModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="modal-body ppa-billing-modal-body">
              <div className="ppa-billing-panel-header">
                <div>
                  <p className="ppa-billing-panel-sub">
                    Eligibility across all {cptTiles.length} billing codes for this practice
                  </p>
                </div>
                <div className="ppa-billing-panel-summary">
                  <div className="ppa-readiness-ring" aria-hidden="true">
                    <svg viewBox="0 0 54 54" width="54" height="54">
                      <circle cx="27" cy="27" r="23" fill="none" stroke="#F1F0EC" strokeWidth="6" />
                      <circle
                        cx="27"
                        cy="27"
                        r="23"
                        fill="none"
                        stroke="#F59E0B"
                        strokeWidth="6"
                        strokeDasharray={`${billingRingCircumference} ${billingRingCircumference}`}
                        strokeDashoffset={billingRingOffset}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="ppa-readiness-ring-text">{billingSummary.pct}%</div>
                  </div>
                  <div className="ppa-billing-panel-summary-text">
                    <p className="ppa-billing-panel-num">
                      {billingSummary.matches} eligible matches
                    </p>
                    <p className="ppa-billing-panel-cap">
                      across {billingSummary.patients} patients
                    </p>
                  </div>
                </div>
              </div>

              <div className="ppa-billing-panel-codes">
                {cptTiles.map((tile) => {
                  const tileTotal = (tile.eligible ?? 0) + (tile.ineligible ?? 0);
                  const eligPct = tileTotal > 0 ? ((tile.eligible ?? 0) / tileTotal) * 100 : 0;
                  const ineligPct = tileTotal > 0 ? 100 - eligPct : 100;
                  return (
                    <div key={tile.code} className="ppa-billing-code-row">
                      <div className="ppa-billing-code-meta">
                        {tile.bucketKey ? (
                          <button
                            type="button"
                            className="ppa-billing-code-name ppa-billing-code-name--link"
                            onClick={() => handleCptBillingCodeClick(tile)}
                          >
                            {tile.code}
                          </button>
                        ) : (
                          <span className="ppa-billing-code-name">{tile.code}</span>
                        )}
                        {tile.desc ? (
                          <span className="ppa-billing-code-desc" title={tile.desc}>
                            {tile.desc}
                          </span>
                        ) : null}
                      </div>
                      <div className="ppa-billing-bar-track">
                        <div className="ppa-billing-bar-elig" style={{ width: `${eligPct}%` }} />
                        <div className="ppa-billing-bar-inelig" style={{ width: `${ineligPct}%` }} />
                      </div>
                      <span className="ppa-billing-code-counts">
                        <strong>{tile.eligible ?? 0}</strong> elig · {tile.ineligible ?? 0} inelig
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Patient Stepper Modal */}
      {showAddPatientModal && (
        <AddPatientModal
          practiceId={practiceId}
          patientToEdit={patientToEdit}
          defaultProgramTab={activeProgramTab}
          onClose={() => {
            setShowAddPatientModal(false);
            setPatientToEdit(null);
          }}
          onSave={() => {
            setShowAddPatientModal(false);
            setPatientToEdit(null);
            practiceService.invalidatePracticePatientCaches(practiceId);
            const params = {
              page: currentPage,
              limit: resolvePatientListLimit(entriesPerPage),
              search: searchTerm,
              status: localStatusFilter,
              sortBy,
              sortOrder,
              program: activeProgramTab
            };
            dispatch(fetchPracticePatientsFilteredByProgramTab({ id: practiceId, params }));
            setRefreshTrigger((prev) => prev + 1);
            window.dispatchEvent(new CustomEvent('evitals:practice-patients-changed', {
              detail: { practiceId }
            }));
            // Immediate CPT/KPI refresh (don't wait only on deferred effect).
            void reloadDashboardAnalytics();
          }}
        />
      )}
    </div>
  );
};

export default PatientManagement;